/**
 * ApsViewerPanel — embeds the Autodesk Platform Services Viewer + a side
 * action rail for AI extraction (BoQ / Schedule / Q&A / Validation).
 *
 * Renders as a slide-in right drawer (mirrors HtmlArtifactPanel's UX).
 * Loads the SVF2 derivatives directly from APS using a viewer-only token
 * minted by the backend, so the user's session never sees the broader
 * APS app secret.
 *
 * AI replies stream via SSE from /api/portal/assets/{id}/analyze and are
 * rendered with the same Markdown pipeline as the chat.
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Sparkles, FileText, Calendar, ShieldCheck, MessageCircle, Maximize2, Minimize2, MousePointerClick, Zap, Send, Film, ChevronDown, ChevronRight, Search } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { engineTier } from '../../lib/modelLabel';
import remarkGfm from 'remark-gfm';
import { portalAssetsApi, portalTeamApi } from '../../services/api';
import { usePortalStore } from '../../stores';
import AnimatorPane from './AnimatorPane';
import EngineOptimizerCard from './EngineOptimizerCard';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Map kind → display
const ACTIONS = [
  { kind: 'boq',        label: 'Generate BoQ',  icon: FileText,    desc: 'Bill of Quantities from element data' },
  { kind: 'schedule',   label: 'Schedule',      icon: Calendar,    desc: 'Construction schedule (WBS + durations)' },
  { kind: 'clash',      label: 'Clash detect',  icon: Zap,         desc: 'Hard / soft / workflow interferences' },
  { kind: 'qa',         label: 'Ask',           icon: MessageCircle, desc: 'Free-form Q&A on the model' },
  { kind: 'validation', label: 'Audit',         icon: ShieldCheck, desc: 'Data-quality / property heuristics' },
];

export default function ApsViewerPanel({ asset, open, onClose }) {
  const viewerHostRef = useRef(null);
  const viewerRef = useRef(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [selectedKind, setSelectedKind] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  // Conversation thread for the current asset. Replaces the single
  // `aiOutput` string so users can chat back-and-forth with the model
  // (refine BoQ, tighten schedule, drill into a clash, etc.) without
  // losing prior context. Each entry: {role, content, kind?}.
  const [thread, setThread] = useState([]);
  // Live-streaming assistant chunk (separated from `thread` so we can
  // append it atomically once the SSE stream ends).
  const [streamingAssistant, setStreamingAssistant] = useState('');
  // Multi-turn follow-up input
  const [followUp, setFollowUp] = useState('');
  // Sending-to-chat state for the transfer button
  const [transferring, setTransferring] = useState(false);
  // Properties Browser — lets the user pick which attribute to surface
  // as the "3D Object Link" identifier in BoQ / Schedule outputs
  // (defaults to none → backend uses element name + objectid).
  const [propertyKeys, setPropertyKeys] = useState([]);
  const [mappingAttribute, setMappingAttribute] = useState('');
  // Optional project start date (YYYY-MM-DD). When set, backend produces
  // real calendar Start Date / Finish Date columns instead of "Day N".
  const [projectStartDate, setProjectStartDate] = useState('');
  // Working-calendar controls — when "Skip weekends" is on the LLM must
  // not assign work to Sat/Sun; the holidays box accepts a comma-separated
  // list of ISO dates that are also skipped.
  const [skipWeekends, setSkipWeekends] = useState(true);
  // Configurable weekend days — JS Date.getDay() indices (0=Sun .. 6=Sat).
  const [weekendDays, setWeekendDays] = useState([0, 6]);
  const [holidaysText, setHolidaysText] = useState('');
  // Name → objectid index — resolves friendly names in schedule rows
  // (e.g. /V120, EQU /E1301) so click-to-isolate works even when the
  // user has chosen `Name` as the 3D mapping attribute.
  const [nameIndex, setNameIndex] = useState({});
  // Prescope modal (Feb 2026) — Navisworks-Manage-style pre-run picker
  // for BoQ + Clash Detect. Lets the user narrow the analysis to
  // selected hierarchy branches + property subsets so the LLM doesn't
  // waste tokens on the whole model when they only care about (e.g.)
  // /STABILIZER/PIPES. Selected paths + properties are injected into
  // the analyze prompt as a SCOPE block before streaming.
  const [prescope, setPrescope] = useState({
    open: false,
    kind: null,           // 'boq' | 'clash'
    paths: new Set(),     // objectids of checked hierarchy nodes (Selection A for clash)
    // Second selection set — used ONLY by Navisworks-style Clash Detect.
    // Selection A × Selection B is the pair the LLM tests for
    // interferences. Ignored for BoQ (single-selection scoping).
    pathsB: new Set(),
    // Which selection set the tree checkboxes write to when kind='clash'.
    activeSet: 'A',       // 'A' | 'B'
    // Clash test type — mirrors Navisworks-Manage semantics.
    //   hard      — geometry overlap (default)
    //   clearance — proximity within `tolerance` mm
    //   duplicates — identical externalId / name / geometry pairs
    testType: 'hard',
    tolerance: 25,        // mm — only meaningful for testType='clearance'
    properties: new Set(),// property keys the user wants prioritised
    // Per-key value whitelist for the property drill-down. Shape:
    // { [propertyKey]: Set<value> }. When non-empty for a key, the
    // backend restricts the element catalogue to only elements whose
    // `key` has a value in the picked set — meaning the BoQ / clash /
    // schedule output ONLY covers those elements.
    propertyValues: {},
  });
  const [treeLines, setTreeLines] = useState([]);   // flat hierarchy lines
  // Prescope UX state — chevron-collapse of parent nodes + search boxes
  // for both the tree and the property picker.
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  const [treeSearch, setTreeSearch] = useState('');
  const [propSearch, setPropSearch] = useState('');
  const [propShowAll, setPropShowAll] = useState(false);
  // Value drill-down state — when non-null, the property column shows
  // the value picker for that key instead of the property list. Values
  // are lazy-loaded from /property-values and cached by key.
  const [drilldownKey, setDrilldownKey] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownSearch, setDrilldownSearch] = useState('');
  const [valueCache, setValueCache] = useState({}); // { key: [{value, count}] }

  // Clash Result workflow — server-persisted. Each row is keyed by
  // (asset_id, clash_id); `records` is a map so lookups are O(1) and
  // the UI stays sticky when a teammate updates a status remotely.
  const [clashRecords, setClashRecords] = useState({}); // { [clash_id]: {status, assignee_user_id, assignee: {id,name,email}} }
  const [clashStatusFilter, setClashStatusFilter] = useState(new Set()); // empty = show all
  const [teamMembers, setTeamMembers] = useState([]);
  useEffect(() => {
    if (!asset?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await portalAssetsApi.clashRecords(asset.id);
        if (cancelled) return;
        const map = {};
        (resp.data?.records || []).forEach((r) => { map[r.clash_id] = r; });
        setClashRecords(map);
      } catch (_e) { /* silent — table renders defaults */ }
    })();
    return () => { cancelled = true; };
  }, [asset?.id]);
  const setStatusFor = async (clashId, status, meta = {}) => {
    // Optimistic update — snap the UI immediately, roll back on error.
    const prior = clashRecords[clashId] || null;
    setClashRecords((p) => ({ ...p, [clashId]: { ...(p[clashId] || {}), clash_id: clashId, status } }));
    try {
      const resp = await portalAssetsApi.upsertClashRecord(asset.id, clashId, { status, ...meta });
      if (resp.data?.record) {
        setClashRecords((p) => ({ ...p, [clashId]: resp.data.record }));
      }
    } catch (e) {
      setClashRecords((p) => ({ ...p, [clashId]: prior || undefined }));
      toast.error(`Couldn't update status for ${clashId}`);
    }
  };
  const setAssigneeFor = async (clashId, userId, meta = {}) => {
    const prior = clashRecords[clashId] || null;
    setClashRecords((p) => ({ ...p, [clashId]: { ...(p[clashId] || {}), clash_id: clashId, assignee_user_id: userId || null } }));
    try {
      const resp = await portalAssetsApi.upsertClashRecord(asset.id, clashId, { assignee_user_id: userId || null, ...meta });
      if (resp.data?.record) {
        setClashRecords((p) => ({ ...p, [clashId]: resp.data.record }));
        if (userId && userId !== prior?.assignee_user_id) {
          const who = resp.data.record.assignee;
          toast.success(`Assigned ${clashId} to ${who?.name || who?.email || 'teammate'} — email sent`);
        }
      }
    } catch (e) {
      setClashRecords((p) => ({ ...p, [clashId]: prior || undefined }));
      toast.error(`Couldn't assign ${clashId}`);
    }
  };

  // Debounced server-side search for the value drill-down. When the user
  // types in the drilldown search box, if the query isn't already cached
  // we re-fetch from the backend with `?search=` so we can find values
  // OUTSIDE the first 500 (a property like "Name" has 4647 unique
  // values in a Pdms model — client-side filter can't find them all).
  useEffect(() => {
    if (!drilldownKey) return;
    const q = drilldownSearch.trim();
    if (!q) return;
    const cacheKey = `${drilldownKey}::${q.toLowerCase()}`;
    if (valueCache[cacheKey]) return;
    let cancelled = false;
    setDrilldownLoading(true);
    const timer = setTimeout(async () => {
      try {
        const resp = await portalAssetsApi.propertyValues(asset.id, drilldownKey, { search: q, limit: 500 });
        if (cancelled) return;
        setValueCache((prev) => ({
          ...prev,
          [cacheKey]: {
            values: resp.data?.values || [],
            total_unique: resp.data?.total_unique || 0,
          },
        }));
      } catch (_e) {
        // toast already fired on open failure; silently keep prior list
      } finally {
        if (!cancelled) setDrilldownLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [drilldownSearch, drilldownKey, asset?.id, valueCache]);

  // Presets — persisted named prescope selections so a returning user
  // doesn't have to re-tick 40 branches every time. Keyed by asset id so
  // each model gets its own set. Stored in localStorage (no backend
  // schema change needed for the MVP).
  const _PRESET_KEY = asset?.id ? `capex-prescope-presets:${asset.id}` : '';
  const [presets, setPresets] = useState(() => {
    try { return JSON.parse(localStorage.getItem(_PRESET_KEY) || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    if (!_PRESET_KEY) return;
    try { setPresets(JSON.parse(localStorage.getItem(_PRESET_KEY) || '[]')); } catch { setPresets([]); }
  }, [_PRESET_KEY]);
  const saveScopePreset = () => {
    const name = window.prompt('Preset name (e.g. "Piping only"):');
    if (!name) return;
    const next = [
      ...presets.filter((p) => p.name !== name),
      {
        name,
        paths: [...prescope.paths],
        properties: [...prescope.properties],
        propertyValues: Object.fromEntries(
          Object.entries(prescope.propertyValues || {}).map(([k, vs]) => [k, [...vs]]),
        ),
        created_at: Date.now(),
      },
    ];
    setPresets(next);
    try { localStorage.setItem(_PRESET_KEY, JSON.stringify(next)); } catch { /* quota — ignore */ }
    toast.success(`Preset "${name}" saved`);
  };
  const applyScopePreset = (preset) => {
    setPrescope((p) => ({
      ...p,
      paths: new Set(preset.paths || []),
      properties: new Set(preset.properties || []),
      propertyValues: Object.fromEntries(
        Object.entries(preset.propertyValues || {}).map(([k, vs]) => [k, new Set(vs)]),
      ),
    }));
    toast.info(`Applied preset "${preset.name}"`);
  };
  const deleteScopePreset = (name) => {
    const next = presets.filter((p) => p.name !== name);
    setPresets(next);
    try { localStorage.setItem(_PRESET_KEY, JSON.stringify(next)); } catch { /* */ }
  };

  // Live search-hit highlighter — wraps every occurrence of `q` inside
  // `text` in a <mark> so a search in the prescope tree makes matches
  // instantly visible. Falls back to plain text when `q` is empty.
  const _highlight = (text, q) => {
    const t = String(text || '');
    const query = String(q || '').trim();
    if (!query) return t;
    try {
      const rx = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig');
      const parts = [];
      let last = 0;
      let m;
      while ((m = rx.exec(t)) !== null) {
        if (m.index > last) parts.push(t.slice(last, m.index));
        parts.push(<mark key={`mk-${m.index}`} style={{ background: 'rgba(242,107,31,0.35)', color: 'inherit', padding: '0 1px', borderRadius: 2 }}>{m[0]}</mark>);
        last = m.index + m[0].length;
        if (m[0].length === 0) rx.lastIndex += 1;   // avoid infinite loop on zero-width matches
      }
      if (last < t.length) parts.push(t.slice(last));
      return parts;
    } catch { return t; }
  };

  // Property-type icon inferred from the first sample value. Boolean →
  // ✓, numeric → # (with a unit hint if the sample has letters after
  // the number), everything else → T (text). Keeps the property panel
  // scannable in a 400-key model.
  const _propTypeIcon = (p) => {
    const s = (p?.samples || [])[0];
    if (typeof s === 'boolean' || /^(true|false|yes|no)$/i.test(String(s || ''))) {
      return <span title="Boolean" className="inline-block w-3 text-center opacity-70">✓</span>;
    }
    if (typeof s === 'number' || /^-?\d+(\.\d+)?/.test(String(s || ''))) {
      const hasUnit = /[a-z]+$/i.test(String(s || '').trim());
      return <span title={hasUnit ? 'Numeric + unit' : 'Numeric'} className="inline-block w-3 text-center opacity-70 font-mono">#</span>;
    }
    return <span title="Text" className="inline-block w-3 text-center opacity-70 font-mono">T</span>;
  };
  // Draggable AI rail width (% of drawer)
  const [aiRailPct, setAiRailPct] = useState(38);
  const draggingRef = useRef(false);
  // Animate-timeline playback
  const [isPlaying, setIsPlaying] = useState(false);
  const playTimerRef = useRef(null);
  // Animator modal — which message's table is being configured for animation.
  // `null` = closed. Number = index into `thread`.
  const [animatorMsgIdx, setAnimatorMsgIdx] = useState(null);
  // Continuation progress — increments each time the backend yields a
  // `<!-- continuation #N -->` heartbeat (auto-continue between Opus legs).
  // Cleared at the start of each new analyze stream. Lets the user see
  // that the SSE is still alive during long per-leg inference pauses.
  const [continuationN, setContinuationN] = useState(0);

  // ── Window-level drag handlers ────────────────────────────────────────
  // The APS Viewer canvas consumes pointer events, so a `onMouseMove`
  // attached to a parent <div> never fires once the cursor crosses into
  // the WebGL surface. We attach the move/up listeners to `window`
  // while the divider is being dragged — guarantees smooth resize over
  // any child element including the viewer canvas + the markdown table.
  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const body = document.querySelector('[data-testid="aps-split-body"]');
      if (!body) return;
      const rect = body.getBoundingClientRect();
      const pct = ((rect.right - e.clientX) / rect.width) * 100;
      setAiRailPct(Math.max(22, Math.min(70, pct)));
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);
  // Saved analyses (history) for this asset
  const [savedRuns, setSavedRuns] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Cumulative credit consumption for this asset — sourced from the
  // backend ledger so it matches the wallet 1:1. Refreshed after every
  // successful run.
  const [creditSummary, setCreditSummary] = useState(null);
  // Engine Optimizer recommendation — surfaces a cheaper engine for
  // routine work when enough usage signal has accumulated.
  const [engineAdvice, setEngineAdvice] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  // Captured by the APS Viewer's SELECTION_CHANGED_EVENT. When set, the
  // next analyze call sends `element_id` and the backend prepends that
  // element's full property tree to the LLM prompt — letting users
  // literally click a beam and ask "what's the spec on this".
  const [selectedElement, setSelectedElement] = useState(null);  // { dbId, name }
  const abortRef = useRef(null);

  const language = (() => {
    try { return localStorage.getItem('cognara.chat.lang') || 'en'; } catch { return 'en'; }
  })();
  const selectedModel = (() => {
    try { return localStorage.getItem('cognara.chat.model') || ''; } catch { return ''; }
  })();

  // Initialise the viewer when the drawer opens
  useEffect(() => {
    if (!open || !asset || !asset.urn) return;
    if (asset.status !== 'success') return;
    let cancelled = false;

    const init = async () => {
      if (!window.Autodesk || !window.Autodesk.Viewing) {
        setViewerError('APS Viewer SDK is still loading. Please retry in a moment.');
        return;
      }
      if (!viewerHostRef.current) return;
      setViewerError(null);
      try {
        const options = {
          env: 'AutodeskProduction2',
          api: 'streamingV2',
          getAccessToken: async (onTokenReady) => {
            try {
              const { data } = await portalAssetsApi.viewerToken();
              onTokenReady(data.access_token, data.expires_in || 3500);
            } catch (e) {
              console.error('viewer-token fetch failed', e);
              setViewerError('Unable to fetch APS viewer token. Check your auth and try again.');
            }
          },
        };
        await new Promise((resolve) => window.Autodesk.Viewing.Initializer(options, resolve));
        if (cancelled) return;
        const v = new window.Autodesk.Viewing.GuiViewer3D(viewerHostRef.current);
        v.start();
        viewerRef.current = v;

        // Capture user selection so the AI rail can drill down into a
        // single element. The viewer fires SELECTION_CHANGED_EVENT with
        // an array of dbIds; we resolve the first one's display name via
        // viewer.getProperties().
        const SEL_EVT = window.Autodesk.Viewing?.SELECTION_CHANGED_EVENT
                       || 'selectionChanged';
        const onSelect = (ev) => {
          const ids = ev?.dbIdArray || ev?.selections?.[0]?.dbIdArray || [];
          if (!ids.length) {
            setSelectedElement(null);
            return;
          }
          const dbId = ids[0];
          try {
            v.getProperties(dbId, (props) => {
              if (cancelled) return;
              setSelectedElement({ dbId, name: props?.name || `Element ${dbId}` });
            }, () => setSelectedElement({ dbId, name: `Element ${dbId}` }));
          } catch {
            setSelectedElement({ dbId, name: `Element ${dbId}` });
          }
        };
        v.addEventListener(SEL_EVT, onSelect);
        // Stash for cleanup
        v._cognaraOnSelect = { evt: SEL_EVT, fn: onSelect };

        // Keep the viewer canvas sized to the host. When the user toggles
        // fullscreen or expands the browser window, basis% changes and the
        // canvas would otherwise stretch with whitespace until the next
        // mouse-drag. ResizeObserver fixes that.
        if (viewerHostRef.current && 'ResizeObserver' in window) {
          const ro = new ResizeObserver(() => {
            try { v.resize(); } catch {/* viewer torn down */}
          });
          ro.observe(viewerHostRef.current);
          v._cognaraResizeObs = ro;
        }

        const docId = `urn:${asset.urn}`;
        window.Autodesk.Viewing.Document.load(
          docId,
          (doc) => {
            if (cancelled) return;
            const defaultModel = doc.getRoot().getDefaultGeometry();
            v.loadDocumentNode(doc, defaultModel).then(() => {
              if (!cancelled) setViewerReady(true);
            }).catch((err) => setViewerError(`Load error: ${err?.message || err}`));
          },
          (code, msg) => setViewerError(`Document load failed (${code}): ${msg}`),
        );
      } catch (e) {
        console.error(e);
        setViewerError(`Init error: ${e?.message || e}`);
      }
    };
    init();

    return () => {
      cancelled = true;
      try {
        const v = viewerRef.current;
        if (v?._cognaraOnSelect) {
          v.removeEventListener(v._cognaraOnSelect.evt, v._cognaraOnSelect.fn);
        }
        try { v?._cognaraResizeObs?.disconnect(); } catch {/* */}
        v?.finish();
      } catch {/* */}
      viewerRef.current = null;
      setViewerReady(false);
      setSelectedElement(null);
    };
  }, [open, asset?.urn, asset?.status]);

  // Load Properties Browser + saved-runs list when the asset is ready.
  useEffect(() => {
    if (!open || !asset?.id || asset.status !== 'success') return;
    let cancelled = false;
    (async () => {
      try {
        const [pk, runs, ni, cs, ea, tm] = await Promise.all([
          portalAssetsApi.propertyKeys(asset.id),
          portalAssetsApi.listAnalyses(asset.id),
          portalAssetsApi.nameIndex(asset.id),
          portalAssetsApi.creditSummary(asset.id).catch(() => ({ data: null })),
          portalAssetsApi.engineAdvice(asset.id).catch(() => ({ data: null })),
          portalTeamApi.members().catch(() => ({ data: { members: [] } })),
        ]);
        if (cancelled) return;
        setPropertyKeys(pk.data?.keys || []);
        setSavedRuns(runs.data?.runs || []);
        setNameIndex(ni.data?.index || {});
        if (cs?.data) setCreditSummary(cs.data);
        if (ea?.data) setEngineAdvice(ea.data);
        setTeamMembers(tm.data?.members || []);
      } catch (e) {
        console.warn('Property keys / analyses / name-index load failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [open, asset?.id, asset?.status]);

  // Deep-link — auto-load the run referenced by `?run=kind:sid` in the
  // URL when the saved-runs list finishes loading. Runs once per
  // asset-load; if the run has already been rehydrated we skip.
  useEffect(() => {
    if (!open || !asset?.id || asset.status !== 'success') return;
    if (!savedRuns?.length) return;
    if (thread.length > 0) return;                 // user already has a thread — don't clobber
    try {
      const params = new URLSearchParams(window.location.search);
      const runParam = params.get('run');
      if (!runParam) return;
      const [kind, sid = 'default'] = runParam.split(':');
      const match = savedRuns.find((r) => r.kind === kind && (r.session_id || 'default') === sid);
      if (match) loadSavedRun(match);
    } catch (_e) { /* URL parse failure — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id, savedRuns]);

  // Stop any in-flight stream on close
  useEffect(() => {
    if (!open) {
      try { abortRef.current?.abort(); } catch {/* */}
      abortRef.current = null;
      setIsStreaming(false);
    }
  }, [open]);

  // Generic SSE caller shared by `runAnalysis` (first turn) and
  // `runFollowUp` (subsequent turns). Streams the assistant chunk into
  // `streamingAssistant`, then commits a final {role:'assistant'} entry
  // to `thread` once the SSE terminator fires.
  //
  // ── Chunked-pagination resilience ─────────────────────────────────
  // Cloudflare's edge proxy enforces a 60-second timeout on SSE HTTP/2
  // responses on the preview/free tier. To produce schedules with
  // 5000+ rows we cannot stay on a single HTTP request — the backend
  // gracefully closes each response at ~45s with a `chunk_end` event;
  // we detect that here and IMMEDIATELY re-POST a continuation request
  // with the accumulated text as `history`. To the user, it looks like
  // one seamless stream that runs as long as needed.
  const streamCall = async ({ kind, userPrompt, includeHistory, propertyFilter, clashTest }) => {
    setIsStreaming(true);
    setStreamingAssistant('');
    setContinuationN(0);  // reset continuation counter for this new stream
    const controller = new AbortController();
    abortRef.current = controller;
    let finalText = '';
    // Captured from the SSE `done` event so the AnimatorPane / viewer
    // can render the credit receipt chip alongside the analysis result.
    let finalMeter = null;
    // Chunk counter — guard against runaway resume loops.
    let chunkIdx = 0;
    const MAX_CHUNKS = 20;  // ~20 × 45s = ~15min of total inference. Cap
                            // tightened Feb 2026 to prevent a runaway
                            // schedule that never emits SCHEDULE_COMPLETE
                            // from draining the tenant's wallet.

    const doOneChunk = async (resumeFromContent) => {
      const token = usePortalStore.getState().token;
      const body = {
        kind, language,
        model: selectedModel || undefined,
        element_id: selectedElement?.dbId ?? undefined,
        mapping_attribute: mappingAttribute || undefined,
        session_id: 'default',
        project_start_date: projectStartDate || undefined,
        skip_weekends: projectStartDate ? !!skipWeekends : undefined,
        weekend_days: (projectStartDate && skipWeekends && weekendDays.length)
          ? weekendDays.slice().sort() : undefined,
        holidays: projectStartDate && holidaysText
          ? holidaysText.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 60)
          : undefined,
        // Prescope value drill-down — { propertyKey: [values] }. Backend
        // filters the element catalogue to only elements matching every
        // pair, so BoQ / schedule / clash rows are restricted to those
        // elements.
        property_filter: (propertyFilter && Object.keys(propertyFilter).length)
          ? propertyFilter : undefined,
        // Navisworks-style clash test payload. Backend expands it into
        // an authoritative "Selection A × Selection B" clash prompt.
        clash_test: clashTest || undefined,
      };
      if (resumeFromContent) {
        // Continuation request — feed the partial output back as the
        // most recent assistant turn so the model picks up where the
        // previous chunk left off. We extract the LAST emitted
        // Activity ID + task-name and inject it as an explicit anchor
        // into the prompt so the model knows precisely where to
        // resume (Claude tends to silently duplicate rows otherwise).
        const baseHist = includeHistory
          ? thread.map((t) => ({ role: t.role, content: t.content }))
          : [];
        body.history = [...baseHist, { role: 'assistant', content: resumeFromContent.slice(-25000) }];
        // Pull the latest table row from the accumulated content.
        const tableRows = (resumeFromContent.match(/^\|[^\n]+\|$/gm) || [])
          .filter((r) => !/^\|[\s|:-]+\|$/.test(r))   // drop separators
          .filter((r) => {
            // Drop header rows (where the first cell looks like a label)
            const firstCell = r.split('|')[1]?.trim().toLowerCase() || '';
            return !['activity id', 'activity', 'item', 'wbs', 'task name', 'category'].includes(firstCell);
          });
        const lastRow = tableRows[tableRows.length - 1] || '';
        const lastCells = lastRow.split('|').map((c) => c.trim()).filter(Boolean);
        const lastActivityId = lastCells[0] || '';
        const lastTaskName = (lastCells[3] || lastCells[1] || '').slice(0, 80);
        const lastRowsSummary = tableRows.slice(-3)
          .map((r) => r.split('|').slice(1, 5).map((c) => c.trim()).join(' | '))
          .join('\n');
        body.prompt = (
          'Continue EXACTLY from where you stopped in your previous turn.\n\n' +
          (lastActivityId ? `Your last emitted row was: **${lastActivityId}** — ${lastTaskName}\n\n` : '') +
          (lastRowsSummary ? `The last 3 rows you emitted (for context, do NOT repeat):\n\`\`\`\n${lastRowsSummary}\n\`\`\`\n\n` : '') +
          'STRICT RULES (format contract — non-negotiable):\n' +
          '1. The very next row you emit MUST have a NEW Activity ID — strictly greater than ' +
          (lastActivityId || 'any ID you have already used') + '. Increment by 10 from the previous ID.\n' +
          '2. Do NOT re-emit the table header / separator row.\n' +
          '3. Do NOT repeat any row already emitted in your previous turn(s).\n' +
          '4. Every row MUST be on its OWN line — hard newline (`\\n`) after the closing `|`. NEVER concatenate multiple rows onto one line — this breaks the markdown parser and produces unreadable pipe-delimited soup.\n' +
          '5. Every row MUST start with `|` and end with `|`, with columns separated by ` | ` (space-pipe-space).\n' +
          '6. Keep going until every catalogue element has been scheduled, then emit ' +
          '`<!-- SCHEDULE_COMPLETE -->` on its own final line.\n' +
          '7. If the catalogue is already exhausted, emit the Catalogue Coverage Note + sentinel.'
        );
      } else if (includeHistory) {
        body.history = thread.map((t) => ({ role: t.role, content: t.content }));
        body.prompt = userPrompt;
      } else {
        body.prompt = userPrompt || prompt;
      }
      const resp = await fetch(`${API_BASE}/api/portal/assets/${asset.id}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt?.slice(0, 300) || `${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // Per-chunk outcome: 'done', 'chunk_end' (resume), 'error', 'eof'.
      let outcome = 'eof';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (!payload) continue;
          let evt;
          try { evt = JSON.parse(payload); }
          catch { continue; }
          if (evt.type === 'done') {
            outcome = 'done';
            // Capture the credit-deduction breakdown so we can render
            // the same receipt chip used in chat. Survives the loop
            // closure via the outer `finalMeter` binding.
            if (evt.meter) finalMeter = evt.meter;
            continue;
          }
          if (evt.type === 'chunk_end') {
            // Backend bailed at the Cloudflare-safe deadline. Mark for
            // resume and exit this chunk's stream loop.
            outcome = 'chunk_end';
            // Bump the AI-rail "continuation" chip so the user can see
            // we're pagination-stitching across HTTP requests.
            setContinuationN((cur) => cur + 1);
            continue;
          }
          if (evt.type === 'error') {
            finalText += `\n\n⚠ ${evt.text || 'stream error'}`;
            setStreamingAssistant(finalText);
            outcome = 'error';
            continue;
          }
          if (evt.type === 'content' && evt.text) {
            finalText += evt.text;
            setStreamingAssistant(finalText);
            const matches = evt.text.matchAll(/<!--\s*continuation\s+#(\d+)\s*-->/g);
            for (const m of matches) {
              const n = parseInt(m[1], 10);
              if (!Number.isNaN(n)) setContinuationN((cur) => Math.max(cur, n));
            }
          }
        }
      }
      return outcome;
    };

    try {
      // First chunk — normal request path.
      let outcome = await doOneChunk(null);
      // Auto-resume while the backend keeps emitting `chunk_end`. Stop
      // when (a) `done`/`error` arrived, (b) we've already seen the
      // SCHEDULE_COMPLETE sentinel in the accumulated content, or
      // (c) the user aborted (controller.signal triggered).
      while (outcome === 'chunk_end'
             && chunkIdx < MAX_CHUNKS
             && !controller.signal.aborted
             && !finalText.includes('<!-- SCHEDULE_COMPLETE -->')) {
        chunkIdx += 1;
        outcome = await doOneChunk(finalText);
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('analyze failed', e);
        toast.error(`Analysis failed: ${e.message || e}`);
        finalText += `\n\n⚠ ${e.message || e}`;
      }
    } finally {
      // Commit the streamed message to the persisted thread.
      if (finalText.trim()) {
        setThread((t) => [...t, { role: 'assistant', content: finalText, kind, meter: finalMeter }]);
        try {
          const runs = await portalAssetsApi.listAnalyses(asset.id);
          setSavedRuns(runs.data?.runs || []);
        } catch { /* non-blocking */ }
        // Refresh the cumulative credit chip after every successful run
        // so it stays in sync with the ledger. Failure is non-fatal —
        // the chip just keeps its last value until the next refresh.
        try {
          const cs = await portalAssetsApi.creditSummary(asset.id);
          if (cs?.data) setCreditSummary(cs.data);
        } catch { /* non-blocking */ }
        // Also refresh engine advice — a new run may have tipped the
        // recommendation in or out, or changed the heaviest engine.
        try {
          const ea = await portalAssetsApi.engineAdvice(asset.id);
          if (ea?.data) setEngineAdvice(ea.data);
        } catch { /* non-blocking */ }
      }
      setStreamingAssistant('');
      setIsStreaming(false);
    }
  };

  const runAnalysis = async (kind) => {
    if (!asset?.id) return;
    if (asset.status !== 'success') {
      toast.error(`Asset not ready (status: ${asset.status || 'pending'}). Wait for translation to finish.`);
      return;
    }
    // BoQ + Clash Detect open the Navisworks-style prescope modal so
    // the user can pick which hierarchy branches + properties matter.
    // Schedule / Ask / Audit continue running full-model as before.
    if (kind === 'boq' || kind === 'clash') {
      // Refresh the asset via GET /assets/{id} so the backend has a
      // chance to lazy-backfill object_tree_flat (iter54 fix). Falls
      // back to the prop value if the refresh call fails — we never
      // want to leave the user staring at an empty picker.
      let liveAsset = asset;
      try {
        const { data } = await portalAssetsApi.get(asset.id);
        if (data?.object_tree_flat?.length) liveAsset = data;
      } catch (_e) { /* ignore — use stale prop */ }
      const raw = liveAsset?.object_tree_flat || [];
      const parsed = raw.map((line) => {
        const depth = (line.match(/^\s*/)?.[0].length || 0) / 2;
        const m = line.match(/\[(\d+)\]\s+(.+?)(?:\s+\(\d+\s+children\))?$/);
        return m ? { depth, objectid: m[1], name: m[2] } : null;
      }).filter(Boolean);
      // Friendly root label — the derivative pipeline sometimes emits a
      // long composite GUID for the top node (e.g.
      // "3a7c4c18…__884eaf65…__Pdms_1.nwd"). Swap it for the asset's
      // filename so the tree reads cleanly.
      if (parsed[0] && /^[a-f0-9]{8}[-_]/i.test(parsed[0].name)) {
        parsed[0] = { ...parsed[0], name: asset?.filename || 'Model root' };
      }
      // Pre-compute the descendant-index range for every node so parent
      // toggles can flip an entire branch in O(N).
      parsed.forEach((n, i) => {
        let end = i + 1;
        while (end < parsed.length && parsed[end].depth > n.depth) end += 1;
        n.descendantEnd = end;   // [i+1, end) is the branch
        n.hasChildren = end > i + 1;
      });
      setTreeLines(parsed);
      // Default collapse rule (Feb 2026 v2) — never collapse the first
      // three levels (root, scene, top-branches like /EQUIP), and
      // collapse deep nodes only when they have MANY descendants.
      // Previous logic ("depth ≥ 3 + tree > 200") collapsed /EQUIP and
      // /PIPES on Pdms_1.nwd, hiding 495 of 500 nodes — reproduced the
      // original bug (iter52 P0). This version guarantees the top of
      // the tree is always visible.
      const initialCollapsed = new Set();
      parsed.forEach((n) => {
        if (n.depth <= 3) return;
        const descendantCount = n.descendantEnd - parsed.indexOf(n) - 1;
        if (n.hasChildren && descendantCount > 50) initialCollapsed.add(n.objectid);
      });
      setCollapsedNodes(initialCollapsed);
      setTreeSearch('');
      setPropSearch('');
      setPropShowAll(false);
      setPrescope({
        open: true, kind,
        paths: new Set(),
        pathsB: new Set(),
        activeSet: 'A',
        testType: 'hard',
        tolerance: 25,
        properties: new Set(),
        propertyValues: {},
      });
      return;
    }
    setSelectedKind(kind);
    setThread([{ role: 'user', content: `Run ${kind.toUpperCase()} analysis${prompt ? ` — ${prompt}` : ''}`, kind }]);
    streamCall({ kind, userPrompt: prompt, includeHistory: false });
  };

  // Called from the Prescope modal's "Run scoped analysis" button.
  // Serialises the user's picks into a SCOPE block that gets prepended
  // to their free-form prompt so the LLM narrows the deliverable
  // without the frontend needing a backend schema change.
  const runScopedAnalysis = () => {
    const { kind, paths, pathsB, testType, tolerance, properties, propertyValues } = prescope;
    if (!kind) return;
    const pickedPaths = Array.from(paths);
    const pickedPathsB = Array.from(pathsB);
    const pickedProps = Array.from(properties);
    // Flatten propertyValues → [{ key, values[] }] and also merge each
    // key into pickedProps (so the LLM projects that column even if the
    // user didn't tick the chip explicitly).
    const valueFilters = Object.entries(propertyValues || {})
      .map(([k, vs]) => ({ key: k, values: Array.from(vs || new Set()) }))
      .filter((f) => f.values.length > 0);
    const filterMap = {};
    valueFilters.forEach((f) => { filterMap[f.key] = f.values; });
    valueFilters.forEach((f) => { if (!pickedProps.includes(f.key)) pickedProps.push(f.key); });
    const totalValues = valueFilters.reduce((acc, f) => acc + f.values.length, 0);
    const scopeLines = [];
    const nameFor = (oid) => {
      const line = treeLines.find((t) => t.objectid === oid);
      return line ? `[${oid}] ${line.name}` : `[${oid}]`;
    };
    if (kind === 'clash') {
      // Navisworks-style clash test scope block. Selection A × B is
      // the pair we test for interferences. When either set is empty,
      // fall back to "entire model" for that side.
      const sideA = pickedPaths.length ? pickedPaths.slice(0, 60).map(nameFor).join(', ') : 'ENTIRE MODEL';
      const sideB = pickedPathsB.length ? pickedPathsB.slice(0, 60).map(nameFor).join(', ') : 'ENTIRE MODEL';
      const testLabel = { hard: 'HARD (geometry overlap)', clearance: `CLEARANCE (< ${tolerance} mm)`, duplicates: 'DUPLICATES (identical id/name/geometry)' }[testType] || testType.toUpperCase();
      scopeLines.push(
        `CLASH TEST — Type: ${testLabel}. ` +
        `Selection Set A: ${sideA}. ` +
        `Selection Set B: ${sideB}. ` +
        `Report ONLY interferences between an element in A and an element in B ` +
        (testType === 'clearance'
          ? `whose bounding-boxes come within ${tolerance} mm of each other. `
          : testType === 'duplicates'
            ? `where identical externalId / name / geometry appears in both. `
            : `whose geometry overlaps (Navisworks Hard clash). `) +
        `Do NOT include A-A or B-B pairs. Group results by severity (Critical → Low) ` +
        `and include a Test-Type column and, for clearance tests, a Distance column.`
      );
    } else if (pickedPaths.length) {
      scopeLines.push(`Restrict this ${kind.toUpperCase()} to ONLY the following hierarchy branches (and their descendants): ${pickedPaths.slice(0, 40).map(nameFor).join(', ')}. Ignore every element outside these branches.`);
    }
    if (pickedProps.length) {
      scopeLines.push(`Project ONLY these properties per row: ${pickedProps.slice(0, 20).join(', ')}. Skip other property columns.`);
    }
    if (valueFilters.length) {
      const parts = valueFilters.map((f) => {
        const shown = f.values.slice(0, 12).map((v) => `"${v}"`).join(', ');
        const more = f.values.length > 12 ? ` … (+${f.values.length - 12} more)` : '';
        return `${f.key} ∈ { ${shown}${more} }`;
      });
      scopeLines.push(`HARD FILTER — Include ONLY elements where ${parts.join(' AND ')}. Do NOT include any element that fails these conditions.`);
    }
    const scopedPrompt = [
      scopeLines.length ? `SCOPE — ${scopeLines.join(' ')}` : '',
      prompt || '',
    ].filter(Boolean).join('\n\n');
    setPrescope((p) => ({ ...p, open: false }));
    setSelectedKind(kind);
    const filterSummary = valueFilters.length
      ? ` · ${valueFilters.length} value filter${valueFilters.length === 1 ? '' : 's'} (${totalValues} value${totalValues === 1 ? '' : 's'})`
      : pickedProps.length ? ` · ${pickedProps.length} property filter${pickedProps.length === 1 ? '' : 's'}` : '';
    const clashSummary = kind === 'clash'
      ? ` · ${testType} test${testType === 'clearance' ? ` @ ${tolerance}mm` : ''} · A=${pickedPaths.length || 'all'} × B=${pickedPathsB.length || 'all'}`
      : '';
    const branchSummary = kind === 'clash'
      ? ''
      : (pickedPaths.length ? ` on ${pickedPaths.length} branch${pickedPaths.length === 1 ? '' : 'es'}` : ' (full model)');
    setThread([{
      role: 'user',
      content: `Run ${kind.toUpperCase()} analysis${branchSummary}${clashSummary}${filterSummary}${prompt ? ` — ${prompt}` : ''}`,
      kind,
    }]);
    const clashTest = kind === 'clash' ? {
      selection_a_paths: pickedPaths.map(String),
      selection_b_paths: pickedPathsB.map(String),
      selection_a_names: pickedPaths.map((oid) => nameFor(oid)),
      selection_b_names: pickedPathsB.map((oid) => nameFor(oid)),
      test_type: testType,
      tolerance_mm: testType === 'clearance' ? Number(tolerance) || 25 : null,
    } : null;
    streamCall({ kind, userPrompt: scopedPrompt, includeHistory: false, propertyFilter: filterMap, clashTest });
  };

  const runFollowUp = () => {
    const text = followUp.trim();
    if (!text || isStreaming || !selectedKind) return;
    setThread((t) => [...t, { role: 'user', content: text }]);
    setFollowUp('');
    // Defer so the optimistic user bubble paints before the stream starts.
    setTimeout(() => streamCall({ kind: selectedKind, userPrompt: text, includeHistory: true }), 0);
  };

  const stopAnalysis = () => {
    try { abortRef.current?.abort(); } catch {/* */}
    setIsStreaming(false);
  };

  // Send the current thread to /portal/chat as a fresh conversation.
  const transferToChat = async () => {
    if (!thread.length || transferring) return;
    setTransferring(true);
    try {
      const kindTitle = (selectedKind || 'analysis').toUpperCase();
      const title = `${kindTitle} · ${asset.filename}`.slice(0, 110);
      // Prepend a context preamble so the chat surface knows where this came from.
      const preamble = {
        role: 'assistant',
        content: `📐 **Imported from 3D Assets — ${asset.filename}**\n\nThis conversation was started from a ${kindTitle.toLowerCase()} analysis of your 3D model. Continue refining below — Cognara remembers the full transcript.`,
      };
      const msgs = [preamble, ...thread];
      const { data } = await (await import('../../services/api')).portalConversationsApi.seed({
        title,
        messages: msgs,
        source: { kind: `aps_${selectedKind}`, asset_id: asset.id, filename: asset.filename },
      });
      toast.success('Sent to Knowledge Chat');
      // Hand off — navigate the parent shell to the new conversation.
      window.location.href = `/portal/chat?conversation=${data.id}`;
    } catch (e) {
      console.error('transfer failed', e);
      toast.error(`Transfer failed: ${e?.response?.data?.detail || e.message || e}`);
    } finally {
      setTransferring(false);
    }
  };

  // Track which message is currently being animated so each per-message
  // button can show a distinct ▶/⏸ state.
  const [playingMsgIdx, setPlayingMsgIdx] = useState(null);

  // Parse markdown body-rows out of an assistant message. When the
  // message contains MULTIPLE tables (e.g. the model emitted an
  // Element Catalogue table followed by the actual Schedule), we
  // prefer the **schedule** table — identified by header keywords
  // like "Activity ID", "WBS", "Task Name", "Outline Level". Falls
  // back to the largest table if no schedule-shaped header is found,
  // so non-schedule callers (BoQ, clash, validation) still work.
  const parseTableRows = (content) => {
    const lines = (content || '').split('\n');
    // Split into table blocks: each block is { header, rows[] }.
    const tables = [];
    let cur = null;
    let pendingHeader = null;
    for (const ln of lines) {
      const trimmed = ln.trim();
      if (!trimmed.startsWith('|')) {
        // End of any current table.
        if (cur) { tables.push(cur); cur = null; }
        pendingHeader = null;
        continue;
      }
      if (/^\|[\s-:|]+\|$/.test(trimmed)) {
        // Separator line — the previous pipe-line becomes the header,
        // and a new table starts.
        if (pendingHeader) {
          cur = { header: pendingHeader, rows: [] };
          pendingHeader = null;
        }
        continue;
      }
      if (cur) {
        cur.rows.push(ln);
      } else {
        // Could be the header for the next table; keep until we see
        // the separator line that confirms it.
        pendingHeader = ln;
      }
    }
    if (cur) tables.push(cur);
    if (!tables.length) return [];
    // Score each table — schedule headers match more keywords.
    const SCHEDULE_KEYWORDS = ['activity id', 'wbs', 'task name', 'outline level',
                                'start date', 'finish date', 'predecessor', 'successor'];
    const score = (t) => {
      const h = (t.header || '').toLowerCase();
      return SCHEDULE_KEYWORDS.reduce((acc, k) => acc + (h.includes(k) ? 1 : 0), 0);
    };
    const scored = tables.map((t) => ({ t, s: score(t) }));
    // Prefer the table with the highest schedule-keyword score. If
    // none match (all scores 0), return the largest table — useful
    // for BoQ / clash / validation outputs.
    scored.sort((a, b) => (b.s - a.s) || (b.t.rows.length - a.t.rows.length));
    return scored[0].t.rows;
  };

  // Extract EVERY markdown table (in document order) from an assistant
  // message. Returns [{ header, rows[] }, ...]. Used by the XLSX export
  // so multi-table BoQ / clash outputs keep their section structure
  // instead of getting misaligned under the first table's headers
  // (iter56 test-agent finding).
  const parseAllTables = (content) => {
    const lines = (content || '').split('\n');
    const tables = [];
    let cur = null;
    let pendingHeader = null;
    let lastHeading = '';
    for (const ln of lines) {
      const trimmed = ln.trim();
      if (!trimmed.startsWith('|')) {
        if (cur) { tables.push(cur); cur = null; }
        pendingHeader = null;
        const h = trimmed.match(/^#+\s+(.+?)\s*$/);
        if (h) lastHeading = h[1].replace(/[*_`]/g, '').trim();
        continue;
      }
      if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
        if (pendingHeader) {
          cur = { header: pendingHeader, section: lastHeading || '', rows: [] };
          pendingHeader = null;
        }
        continue;
      }
      if (cur) cur.rows.push(ln);
      else pendingHeader = ln;
    }
    if (cur) tables.push(cur);
    return tables;
  };

  // Extract dbIds from a row's "3D Object Link" cell — integer hits + name-resolved hits.
  const _parseRowIds = (row) => {
    const linkCell = row.split('|').slice(-2, -1)[0] || row;
    const integerHits = Array.from(linkCell.matchAll(/\b(\d{2,7})\b/g))
      .map((m) => parseInt(m[1], 10));
    const nameHits = linkCell
      .split(/[,;|]/)
      .map((s) => s.trim().toLowerCase().replace(/^\/+/, ''))
      .filter((s) => s.length >= 2);
    const fromNames = nameHits
      .map((n) => nameIndex[n])
      .filter((n) => typeof n === 'number');
    return [...integerHits, ...fromNames].filter((n) => n > 0);
  };

  // ── Animate a given set of body-rows — auto-walks each activity's
  // 3D elements with a 1.5s delay, accumulating isolation so users see
  // a live 4D construction sequence inside the drawer.
  // When startIdx > 0, rows [0..startIdx-1] are pre-loaded INSTANTLY
  // (single isolate call) as the "already-built" state, then the
  // animation begins at row `startIdx` and walks forward.
  // We track the accumulator in a LOCAL variable (not React state)
  // because each setTimeout step closes over the value at scheduling time.
  const runAnimation = (bodyRows, msgIdx = null, startIdx = 0) => {
    if (isPlaying) {
      clearTimeout(playTimerRef.current);
      setIsPlaying(false);
      setPlayingMsgIdx(null);
      return;
    }
    if (!bodyRows.length) { toast.info('No table rows to animate'); return; }
    const clampedStart = Math.max(0, Math.min(startIdx, bodyRows.length - 1));
    setIsPlaying(true);
    setPlayingMsgIdx(msgIdx);
    setHighlightedActivity(null);
    try { viewerRef.current?.isolate([]); } catch {/* */}
    const accumulatedIds = new Set();
    const labels = [];
    // Pre-load any rows before the chosen start — gives the user 4D
    // context (the model in its "as-built" state up to that activity)
    // without forcing them to watch every earlier step animate.
    for (let k = 0; k < clampedStart; k += 1) {
      const row = bodyRows[k];
      const rowIds = _parseRowIds(row);
      rowIds.forEach((id) => accumulatedIds.add(id));
    }
    if (accumulatedIds.size) {
      const merged = Array.from(accumulatedIds);
      try {
        viewerRef.current?.isolate(merged);
        viewerRef.current?.fitToView(merged);
        setHighlightedActivity({ ids: merged, label: `Preloaded ${clampedStart} prior activities` });
      } catch (e) {
        console.error('preload isolate failed', e);
      }
    }
    let i = clampedStart;
    const step = () => {
      if (i >= bodyRows.length) {
        setIsPlaying(false);
        setPlayingMsgIdx(null);
        toast.success('Timeline complete — full asset assembled');
        return;
      }
      const row = bodyRows[i];
      const cells = row.split('|').map((c) => c.trim());
      const label = cells[4] || cells[1] || `Step ${i + 1}`;
      const rowIds = _parseRowIds(row);
      rowIds.forEach((id) => accumulatedIds.add(id));
      if (rowIds.length) labels.push(label);
      const merged = Array.from(accumulatedIds);
      if (merged.length) {
        try {
          viewerRef.current?.isolate(merged);
          viewerRef.current?.fitToView(merged);
          setHighlightedActivity({ ids: merged, label: labels.join(' + ') });
        } catch (e) {
          console.error('animate isolate failed', e);
        }
      }
      i += 1;
      playTimerRef.current = setTimeout(step, 1500);
    };
    step();
  };

  // Header "▶ Animate" button — picks the latest assistant message
  // that actually contains a parseable table. Kept for backward
  // compatibility; per-message "Animate this" buttons (rendered next
  // to each assistant bubble below) let users target older tables.
  const animateTimeline = () => {
    if (isPlaying) {
      runAnimation([]);  // toggle-off path
      return;
    }
    let targetIdx = -1;
    for (let i = thread.length - 1; i >= 0; i -= 1) {
      const m = thread[i];
      if (m.role === 'assistant' && parseTableRows(m.content).length) {
        targetIdx = i;
        break;
      }
    }
    if (targetIdx < 0) { toast.info('No schedule to animate'); return; }
    runAnimation(parseTableRows(thread[targetIdx].content), targetIdx);
  };

  const resetThread = () => {
    setThread([]);
    setStreamingAssistant('');
    setSelectedKind(null);
    setFollowUp('');
  };

  // Open a previously-saved run from the History panel — restores its
  // full thread + the original mapping attribute it was generated with.
  // Old runs (saved before Phase 45.8) had the giant "=== 3D MODEL CONTEXT
  // ===" user message persisted; we filter those out at load time so
  // the History panel always renders the assistant tables cleanly.
  const loadSavedRun = async (run) => {
    try {
      // Deep-link — write the run into the URL so this exact analysis
      // can be shared with a teammate via copy-paste. `replaceState`
      // (not `pushState`) so the back button returns to /portal/tools
      // rather than pushing a stack of run history.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('asset', asset.id);
        url.searchParams.set('run', `${run.kind}:${run.session_id || 'default'}`);
        window.history.replaceState({}, '', url.toString());
      } catch (_e) { /* URL rewrite is nice-to-have — ignore failure */ }
      const { data } = await portalAssetsApi.getAnalysis(asset.id, run.kind, run.session_id || 'default');
      const filtered = (data.messages || []).filter((m) => {
        if (m.role !== 'user') return true;
        const c = (m.content || '').slice(0, 80);
        return !c.startsWith('=== 3D MODEL CONTEXT ===')
            && !c.startsWith('Generate a ')
            && !c.includes('CONTEXT ===');
      });
      // If filtering stripped the user prompt entirely, prepend a synthetic one.
      const msgs = filtered.length === 0 || filtered[0].role !== 'user'
        ? [{ role: 'user', content: `Run ${data.kind.toUpperCase()} analysis`, kind: data.kind }, ...filtered]
        : filtered.map((m) => ({ role: m.role, content: m.content, kind: data.kind, meter: m.meter || null }));
      setThread(msgs);
      setSelectedKind(data.kind);
      setMappingAttribute(data.mapping_attribute || '');
      setShowHistory(false);
      toast.success(`Loaded ${data.kind.toUpperCase()} run`);
    } catch (e) {
      toast.error('Failed to load saved run');
    }
  };

  const deleteSavedRun = async (run) => {
    try {
      await portalAssetsApi.deleteAnalysis(asset.id, run.kind, run.session_id || 'default');
      setSavedRuns((rs) => rs.filter((r) => !(r.kind === run.kind && r.session_id === run.session_id)));
    } catch {
      toast.error('Failed to delete run');
    }
  };

  // ── Activity ↔ 3D Model interactivity ─────────────────────────────────────
  // Click any table row in the streamed schedule / BoQ → parse the row's
  // text for numeric dbIds (the "3D Object Link" column) and call the
  // APS Viewer's isolate API. Non-linked elements fade automatically;
  // "Show all" restores full visibility.
  const [highlightedActivity, setHighlightedActivity] = useState(null);
  const isolateForActivity = (rowText, label, additive = false) => {
    const v = viewerRef.current;
    if (!v) { toast.info('Viewer not ready yet'); return; }
    const linkCell = rowText.split('|').slice(-2, -1)[0] || rowText;
    // Collect (a) raw integers in the cell + (b) friendly names (any
    // token of length ≥3 that resolves through `nameIndex`).
    const integerHits = Array.from(linkCell.matchAll(/\b(\d{2,7})\b/g))
      .map((m) => parseInt(m[1], 10));
    const nameHits = linkCell
      .split(/[,;|]/)
      .map((s) => s.trim().toLowerCase().replace(/^\/+/, ''))
      .filter((s) => s.length >= 2);
    const fromNames = nameHits
      .map((n) => nameIndex[n])
      .filter((n) => typeof n === 'number');
    const ids = Array.from(new Set([...integerHits, ...fromNames]))
      .filter((n) => n > 0);
    if (!ids.length) {
      toast.info('This activity has no linked 3D elements');
      return;
    }
    try {
      const merged = additive && highlightedActivity
        ? Array.from(new Set([...highlightedActivity.ids, ...ids]))
        : ids;
      const mergedLabel = additive && highlightedActivity
        ? `${highlightedActivity.label} + ${label}` : label;
      v.isolate(merged);
      v.fitToView(merged);
      setHighlightedActivity({ ids: merged, label: mergedLabel });
    } catch (e) {
      console.error('isolate failed', e);
      toast.error('Could not isolate elements');
    }
  };
  const showAll = () => {
    try { viewerRef.current?.isolate([]); } catch {/* */}
    setHighlightedActivity(null);
  };

  // Country-holiday preset library — keyed to the year of `projectStartDate`
  // so users don't have to figure out the right Diwali date manually.
  const COUNTRY_HOLIDAYS = {
    IN: ['01-26 Republic Day', '03-25 Holi', '08-15 Independence Day',
         '10-02 Gandhi Jayanti', '11-12 Diwali', '12-25 Christmas'],
    US: ['01-01 New Year', '01-19 MLK Day', '05-25 Memorial Day',
         '07-04 Independence Day', '09-07 Labor Day', '11-26 Thanksgiving', '12-25 Christmas'],
    EU: ['01-01 New Year', '04-03 Good Friday', '04-06 Easter Monday',
         '05-01 Labour Day', '12-25 Christmas', '12-26 Boxing Day'],
  };
  const applyPreset = (cc) => {
    const yr = (projectStartDate || new Date().toISOString()).slice(0, 4);
    const dates = COUNTRY_HOLIDAYS[cc].map((s) => `${yr}-${s.slice(0, 5)}`);
    setHolidaysText(dates.join(', '));
  };

  if (!open || !asset) return null;

  const isFullscreen = fullscreen;
  const drawerWidth = isFullscreen ? '100vw' : '78vw';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm"
        onClick={onClose}
        data-testid="aps-viewer-overlay"
      />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 280 }}
        style={{ width: drawerWidth }}
        className="fixed top-0 right-0 h-full z-[81] flex flex-col bg-nexus-bg-secondary border-l border-nexus-border-subtle shadow-2xl"
        data-testid="aps-viewer-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-nexus-border-subtle bg-nexus-bg-primary/40">
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles size={16} className="text-nexus-accent-blue flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-nexus-text-primary truncate" data-testid="aps-viewer-filename">{asset.filename}</div>
              <div className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                {asset.status === 'success' ? `${(asset.element_summary?.total_elements || 0).toLocaleString()} elements indexed` : (asset.status || 'pending').toUpperCase()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setFullscreen((f) => !f)}
              className="p-2 rounded-md hover:bg-nexus-bg-tertiary/50 text-nexus-text-secondary"
              title={isFullscreen ? 'Restore' : 'Fullscreen'}
              data-testid="aps-viewer-fullscreen">
              {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
            <button onClick={onClose}
              className="p-2 rounded-md hover:bg-nexus-bg-tertiary/50 text-nexus-text-secondary"
              title="Close"
              data-testid="aps-viewer-close">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body — responsive split (lg+: viewer + drag-resizable rail; <lg: vertical stack).
            Drag handlers live at the window level (see useEffect above) so they fire
            even when the cursor crosses into the APS Viewer canvas. */}
        <div className="flex-1 flex flex-col lg:flex-row min-h-0" data-testid="aps-split-body">
          {/* Viewer pane — flex-1 so it absorbs the drawer width when the
              AI rail collapses or fullscreen toggles. ResizeObserver below
              keeps the APS Viewer canvas in sync.
              `min-w-0` is critical: without it the default `min-width: auto`
              on flex children lets the APS canvas refuse to shrink below
              its content, which would let the AI rail push the viewer to
              0px when wide schedule tables stream in. */}
          <div className="relative bg-black min-h-0 min-w-0 flex-1 lg:max-w-none h-[42vh] lg:h-auto">
            <div ref={viewerHostRef} className="aps-viewer-host absolute inset-0" data-testid="aps-viewer-host" />
            {asset.status !== 'success' && (
              <div className="absolute inset-0 flex items-center justify-center text-center p-8">
                <div>
                  <Loader2 className="mx-auto animate-spin text-nexus-accent-blue mb-3" size={36} />
                  <div className="text-[13px] text-nexus-text-secondary">
                    Translating model… {asset.progress || ''}
                  </div>
                  <div className="text-[11px] text-nexus-text-tertiary mt-1">
                    SVF2 derivatives are being generated. This may take 1–10 minutes for large RVT / NWD files.
                  </div>
                </div>
              </div>
            )}
            {viewerError && (
              <div className="absolute top-3 left-3 right-3 px-3 py-2 rounded-lg bg-red-500/15 border border-red-500/40 text-[12px] text-red-200">
                {viewerError}
              </div>
            )}
            {highlightedActivity && (
              <div className="absolute top-3 left-3 right-3 px-3 py-2 rounded-lg flex items-center justify-between gap-2"
                style={{ background: 'rgba(242,107,31,0.18)', border: '1px solid rgba(242,107,31,0.45)', color: '#FFD9B5' }}
                data-testid="aps-isolation-banner">
                <span className="text-[12px] truncate">
                  <strong>Isolated:</strong> {highlightedActivity.label} <span className="opacity-70">· {highlightedActivity.ids.length} element{highlightedActivity.ids.length === 1 ? '' : 's'}</span>
                </span>
                <button onClick={showAll}
                  className="text-[11px] px-2 py-0.5 rounded border border-orange-300/40 hover:bg-orange-300/10"
                  data-testid="aps-show-all">
                  Show all
                </button>
              </div>
            )}
            {!viewerReady && asset.status === 'success' && !viewerError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="animate-spin text-nexus-accent-blue" size={32} />
              </div>
            )}
          </div>

          {/* Drag handle between viewer and AI rail (lg+ only) */}
          <div
            className="hidden lg:flex w-1.5 flex-shrink-0 cursor-col-resize items-center justify-center group"
            style={{ background: 'rgba(148,163,184,0.08)' }}
            onMouseDown={(e) => {
              draggingRef.current = true;
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
              e.preventDefault();
            }}
            data-testid="aps-split-drag"
            title="Drag to resize"
          >
            <div className="w-0.5 h-12 rounded-full group-hover:bg-nexus-accent-blue/60"
              style={{ background: 'rgba(148,163,184,0.35)' }} />
          </div>

          {/* AI rail — fixed % width (drag handle to its left resizes it).
              `min-w-0 overflow-hidden` is critical: without it the
              wide schedule / BoQ tables (white-space: nowrap) would
              force the rail to grow past its flex-basis and squeeze
              the 3D viewer down to 0px while content streams in. */}
          <div
            className="flex flex-col border-t lg:border-t-0 lg:border-l border-nexus-border-subtle bg-nexus-bg-primary/30 flex-1 lg:flex-none min-h-0 min-w-0 overflow-hidden"
            style={{ flexBasis: `${aiRailPct}%` }}
          >
            <div className="px-4 py-3 border-b border-nexus-border-subtle flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary flex items-center gap-2">
                  <span>Cognara extraction</span>
                  {/* Continuation progress chip — visible only while
                      streaming AND at least one auto-continue heartbeat
                      has arrived. Proves the SSE is alive across slow
                      per-leg inference (each Opus leg ~32K tokens, 3-5min). */}
                  {isStreaming && continuationN > 0 && (
                    <span
                      className="inline-flex items-center gap-1.5 normal-case tracking-normal text-[10.5px] px-1.5 py-0.5 rounded"
                      style={{
                        background: 'linear-gradient(135deg, rgba(242, 107, 31, 0.20), rgba(251, 139, 58, 0.10))',
                        border: '1px solid rgba(242, 107, 31, 0.45)',
                        color: '#F8A66A',
                      }}
                      title="Auto-continuation between Opus generation legs — the schedule is still streaming"
                      data-testid="aps-continuation-chip"
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full inline-block animate-pulse"
                        style={{ background: '#F26B1F' }}
                      />
                      Continuation #{continuationN} · still streaming…
                    </span>
                  )}
                </div>
                <div className="text-[13px] text-nexus-text-secondary mt-1 leading-snug">
                  {thread.length === 0
                    ? 'Run AI passes against the indexed model. Output streams live below.'
                    : `${(selectedKind || '').toUpperCase()} thread · ${thread.filter((m) => m.role === 'assistant').length} response${thread.filter((m) => m.role === 'assistant').length === 1 ? '' : 's'}`}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Cumulative-spend chip — shows total credits burned
                    across every Schedule / BoQ / Clash / Q&A run for
                    THIS asset. Engineering teams iterating on a model
                    use this to see at a glance which kind is most
                    expensive before they decide to regenerate. */}
                {creditSummary && creditSummary.total_credits > 0 && (() => {
                  const total = Number(creditSummary.total_credits || 0);
                  const display = total >= 100 ? total.toFixed(0) : total.toFixed(1);
                  const byKind = creditSummary.by_kind || {};
                  const breakdown = Object.entries(byKind)
                    .sort(([, a], [, b]) => (b.credits || 0) - (a.credits || 0))
                    .map(([k, v]) => `${k.toUpperCase()}: ${Number(v.credits).toFixed(1)} cr (${v.calls} call${v.calls === 1 ? '' : 's'})`)
                    .join('\n');
                  return (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-mono"
                         style={{ background: 'rgba(242,107,31,0.08)', border: '1px solid rgba(242,107,31,0.25)', color: '#F8A66A' }}
                         data-testid="asset-credit-summary-chip"
                         title={`This asset has consumed ${total.toFixed(2)} credits across ${creditSummary.total_calls} call${creditSummary.total_calls === 1 ? '' : 's'}:\n${breakdown}`}>
                      <span className="text-[9px] uppercase tracking-wider opacity-70">This asset</span>
                      <span style={{ color: '#F26B1F', fontWeight: 700 }}>{display} cr</span>
                    </div>
                  );
                })()}
                {thread.length > 0 && (() => {
                  // Find the latest assistant message with a parseable
                  // table — that's what the Timeliner + CSV export
                  // will operate on.
                  let latestTableIdx = -1;
                  for (let k = thread.length - 1; k >= 0; k -= 1) {
                    const mm = thread[k];
                    if (mm.role === 'assistant' && parseTableRows(mm.content).length) {
                      latestTableIdx = k;
                      break;
                    }
                  }
                  if (latestTableIdx < 0) return null;
                  const rows = parseTableRows(thread[latestTableIdx].content);
                  const rowCount = rows.length;
                  const isSchedule = selectedKind === 'schedule';
                  // Strip markdown bold / italic / code markers from cell
                  // content so the workbook is filter/pivot-friendly.
                  const stripMd = (s) => String(s == null ? '' : s)
                    .replace(/\*\*(.+?)\*\*/g, '$1')
                    .replace(/__(.+?)__/g, '$1')
                    .replace(/`([^`]+)`/g, '$1')
                    .trim();
                  const splitPipesClean = (line) => String(line).trim()
                    .replace(/^\|/, '').replace(/\|$/, '')
                    .split('|').map((s) => stripMd(s));
                  // Base-26 column reference (A..Z, AA, AB, ...). The old
                  // String.fromCharCode(65+i) emitted '[' / '\\' / ']' past
                  // column 26 — iter56 test-agent finding.
                  const colLetter = (idx) => {
                    let n = idx;
                    let out = '';
                    while (n >= 0) {
                      out = String.fromCharCode(65 + (n % 26)) + out;
                      n = Math.floor(n / 26) - 1;
                    }
                    return out;
                  };
                  const downloadXlsx = () => {
                    const md = thread[latestTableIdx].content || '';
                    const tables = parseAllTables(md);
                    if (!tables.length) return;
                    // Expand each table into per-table structured data.
                    // Single-cell rows inside a table body (e.g. a lone
                    // '**Process Piping**') become inline section markers
                    // instead of being emitted as blank-cell data rows.
                    const perTable = tables.map((t, tIdx) => {
                      const headers = t.header ? splitPipesClean(t.header) : [];
                      const totalCols = headers.length;
                      const groups = [];
                      let cur = { section: t.section || '', rows: [] };
                      t.rows.forEach((r) => {
                        const cells = splitPipesClean(r);
                        const nonBlank = cells.filter((c) => c && c.length);
                        const isMarker = totalCols > 1
                          && nonBlank.length === 1
                          && (cells.length < totalCols || cells.slice(1).every((c) => !c));
                        if (isMarker) {
                          if (cur.rows.length || cur.section) groups.push(cur);
                          cur = { section: nonBlank[0], rows: [] };
                        } else {
                          cur.rows.push(cells);
                        }
                      });
                      if (cur.rows.length || !groups.length) groups.push(cur);
                      // Drop a duplicated header row (LLMs sometimes echo it).
                      groups.forEach((g) => {
                        if (headers.length && g.rows.length) {
                          const first = g.rows[0];
                          const sameAsHeader = first.length === headers.length
                            && first.every((c, i) => c.toLowerCase() === (headers[i] || '').toLowerCase());
                          if (sameAsHeader) g.rows.shift();
                        }
                      });
                      const fallback = t.section || `Table ${tIdx + 1}`;
                      return { headers, groups, fallbackSection: fallback };
                    });
                    // Decide whether we need a "Section" column: either
                    // multiple tables, or any table has section markers /
                    // a captured heading section.
                    const sectionCount = perTable.reduce((acc, t) => acc + t.groups.filter((g) => g.section).length, 0);
                    const multi = perTable.length > 1;
                    const needSection = multi || sectionCount > 0;
                    // Build unified header list (union across tables) so
                    // multi-table exports stay column-aligned.
                    const seen = new Map();
                    perTable.forEach((t) => t.headers.forEach((h) => {
                      const key = h.toLowerCase();
                      if (!seen.has(key)) seen.set(key, h);
                    }));
                    const unifiedHeaders = needSection
                      ? ['Section', ...seen.values()]
                      : Array.from(seen.values());
                    const unifiedRows = [];
                    perTable.forEach((t) => {
                      const idxMap = t.headers.map((h) => unifiedHeaders.findIndex((u) => u.toLowerCase() === h.toLowerCase()));
                      t.groups.forEach((g) => {
                        const sectionLabel = g.section || t.fallbackSection || '';
                        g.rows.forEach((cells) => {
                          const row = new Array(unifiedHeaders.length).fill('');
                          if (needSection) row[0] = sectionLabel;
                          cells.forEach((cell, i) => {
                            const dst = idxMap[i];
                            if (dst >= 0) row[dst] = cell;
                          });
                          unifiedRows.push(row);
                        });
                      });
                    });
                    const totalRows = unifiedRows.length;
                    const kindLabel = (selectedKind || 'analysis').toUpperCase();
                    const wb = XLSX.utils.book_new();
                    const guidance = [
                      ['Cognara · Scoped Model Analysis'],
                      [''],
                      ['Analysis', kindLabel],
                      ['Asset', asset?.filename || asset?.id || ''],
                      ['Generated', new Date().toISOString()],
                      ['Rows', String(totalRows)],
                      ['Columns', String(unifiedHeaders.length)],
                      ['Tables in message', String(perTable.length)],
                      [''],
                      ['Column reference'],
                      ...unifiedHeaders.map((h, i) => [`  ${colLetter(i)}. ${h}`]),
                      [''],
                    ];
                    if (needSection) {
                      const sectionRollup = [];
                      perTable.forEach((t) => t.groups.forEach((g) => {
                        const label = g.section || t.fallbackSection;
                        sectionRollup.push([`  · ${label} — ${g.rows.length} row${g.rows.length === 1 ? '' : 's'}`]);
                      }));
                      if (sectionRollup.length) {
                        guidance.push(['Section breakdown'], ...sectionRollup, ['']);
                      }
                    }
                    guidance.push(
                      ['How to use this workbook'],
                      ['1. The "Data" sheet holds every row emitted by the AI, in the same order as the chat table.'],
                      needSection
                        ? ['2. Filter the "Section" column (Ctrl+Shift+L) to isolate each markdown table / section from the original message.']
                        : ['2. Filter or pivot the Data sheet as you would any Excel table (Ctrl+Shift+L to toggle filters).'],
                      ['3. Row order maps 1-1 to the 3D scene — cross-reference the Element ID / Object ID column with the viewer.'],
                      ['4. Numeric cells are stored as text (they preserve the AI\'s original formatting including units). Convert with Data → Text to Columns if you need to sum them.'],
                      ['5. Blank cells mean the AI did not have data for that field. Re-run with a narrower Prescope selection to enrich.'],
                      [''],
                      ['Regenerating this deliverable'],
                      ['• Open Cognara → 3D Assets → this asset → Prescope modal.'],
                      ['• Reuse the same branch + property selection to reproduce these rows.'],
                      ['• Save the selection as a Preset so you can rerun with one click next time.'],
                    );
                    const wsGuide = XLSX.utils.aoa_to_sheet(guidance);
                    wsGuide['!cols'] = [{ wch: 90 }];
                    XLSX.utils.book_append_sheet(wb, wsGuide, 'Guidance');
                    const wsData = XLSX.utils.aoa_to_sheet([unifiedHeaders, ...unifiedRows]);
                    if (unifiedHeaders.length) {
                      wsData['!cols'] = unifiedHeaders.map((h, i) => ({
                        wch: Math.min(48, Math.max(
                          String(h).length,
                          ...unifiedRows.map((r) => String(r[i] ?? '').length),
                        ) + 2),
                      }));
                    }
                    XLSX.utils.book_append_sheet(wb, wsData, 'Data');
                    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
                    const fname = `${kindLabel}_${asset?.filename || asset?.id || 'asset'}_${stamp}.xlsx`;
                    XLSX.writeFile(wb, fname);
                    toast.success(`Downloaded ${totalRows} row${totalRows === 1 ? '' : 's'} as ${fname.split('_').slice(-1)[0]}`);
                  };
                  return (
                    <>
                      {isSchedule && (
                      <button
                        onClick={() => setAnimatorMsgIdx(latestTableIdx)}
                        className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border transition-all"
                        style={{
                          background: 'linear-gradient(135deg, rgba(242, 107, 31, 0.18), rgba(251, 139, 58, 0.12))',
                          borderColor: 'rgba(242, 107, 31, 0.45)',
                          color: '#F8A66A',
                          boxShadow: '0 2px 8px rgba(242, 107, 31, 0.15)',
                        }}
                        title={`Open 4D Timeliner — hierarchical WBS picker, ${rowCount} rows`}
                        data-testid="aps-open-timeliner"
                      >
                        <Film size={12} />
                        4D Timeliner · {rowCount}
                      </button>
                      )}
                      <button
                        onClick={downloadXlsx}
                        className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border transition-all border-nexus-border-subtle text-nexus-text-tertiary hover:text-nexus-accent-blue hover:border-nexus-accent-blue/60"
                        title={`Download all ${rowCount} rows as an XLSX workbook with a Guidance sheet`}
                        data-testid="aps-download-xlsx"
                      >
                        <FileText size={12} />
                        Excel · {rowCount}
                      </button>
                    </>
                  );
                })()}
                {thread.length > 0 && (
                <button onClick={animateTimeline}
                  className={`text-[11px] px-2 py-1 rounded border ${isPlaying ? 'bg-orange-500/15 border-orange-500/60 text-orange-300' : 'border-nexus-border-subtle text-nexus-text-tertiary hover:text-nexus-accent-blue'}`}
                  title={isPlaying ? 'Stop animation' : 'Quick-animate the latest table (starts from row 1)'}
                  data-testid="aps-animate-toggle">
                  {isPlaying ? '⏸ Stop' : '▶ Quick'}
                </button>
              )}
              {savedRuns.length > 0 && (
                  <button onClick={() => setShowHistory((s) => !s)}
                    className={`text-[11px] px-2 py-1 rounded border ${showHistory ? 'bg-nexus-accent-blue/15 border-nexus-accent-blue/40 text-nexus-accent-blue' : 'border-nexus-border-subtle text-nexus-text-tertiary hover:text-nexus-text-primary'}`}
                    title="Past runs" data-testid="aps-history-toggle">
                    History · {savedRuns.length}
                  </button>
                )}
                {thread.length > 0 && (
                  <button onClick={resetThread} className="text-[11px] text-nexus-text-tertiary hover:text-nexus-text-primary px-1"
                    title="Start a new analysis" data-testid="aps-reset-thread">
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* History panel — saved runs across all kinds */}
            {showHistory && savedRuns.length > 0 && (
              <div className="border-b border-nexus-border-subtle bg-nexus-bg-tertiary/30 max-h-[200px] overflow-y-auto" data-testid="aps-history-panel">
                {savedRuns.map((r) => (
                  <div key={`${r.kind}-${r.session_id}`} className="px-4 py-2 flex items-center justify-between hover:bg-nexus-bg-tertiary/40 border-b border-nexus-border-subtle/50">
                    <button onClick={() => loadSavedRun(r)} className="flex-1 text-left min-w-0" data-testid={`aps-history-item-${r.kind}`}>
                      <div className="text-[12px] font-semibold text-nexus-text-primary">{(r.kind || '').toUpperCase()}</div>
                      <div className="text-[10px] text-nexus-text-tertiary">
                        {new Date(r.updated_at || r.created_at).toLocaleString()}
                        {r.mapping_attribute ? ` · map:${r.mapping_attribute}` : ''}
                      </div>
                    </button>
                    <button onClick={() => deleteSavedRun(r)}
                      className="p-1 text-nexus-text-tertiary hover:text-red-400" title="Delete run">
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Properties Browser — pick the attribute used for 3D Object Link */}
            {propertyKeys.length > 0 && (
              <div className="px-3 pt-3" data-testid="aps-properties-browser">
                <label className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary block mb-1">
                  3D mapping attribute <span className="opacity-60">(used for &quot;3D Object Link&quot; column)</span>
                </label>
                <select
                  value={mappingAttribute}
                  onChange={(e) => setMappingAttribute(e.target.value)}
                  disabled={isStreaming}
                  className="w-full text-[12px] rounded-md px-2 py-1.5 focus:outline-none focus:border-nexus-accent-blue/60 appearance-none"
                  style={{
                    background: 'var(--nexus-input-bg)',
                    color: 'var(--nexus-text-primary)',
                    border: '1px solid var(--nexus-border-subtle)',
                  }}
                  data-testid="aps-mapping-attribute"
                >
                  <option value="" style={{ background: 'var(--nexus-input-bg)', color: 'var(--nexus-text-primary)' }}>— Default (objectid + name)</option>
                  {propertyKeys.slice(0, 80).map((p) => (
                    <option key={`${p.group}::${p.key}`} value={p.key}
                      style={{ background: 'var(--nexus-input-bg)', color: 'var(--nexus-text-primary)' }}>
                      {p.key} · {p.group} ({p.count}× · e.g. {(p.samples || []).slice(0, 1).join('')})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Project start date — when set, the schedule uses real calendar
                dates instead of "Day N" relative offsets. */}
            {(thread.length === 0 || selectedKind === 'schedule') && (
              <div className="px-3 pt-3" data-testid="aps-startdate-row">
                <label className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary block mb-1">
                  Project start date <span className="opacity-60">(used for Start Date / Finish Date)</span>
                </label>
                <input
                  type="date"
                  value={projectStartDate}
                  onChange={(e) => setProjectStartDate(e.target.value)}
                  disabled={isStreaming}
                  className="w-full text-[12px] rounded-md px-2 py-1.5 focus:outline-none focus:border-nexus-accent-blue/60"
                  style={{
                    background: 'var(--nexus-input-bg)',
                    color: 'var(--nexus-text-primary)',
                    border: '1px solid var(--nexus-border-subtle)',
                  }}
                  data-testid="aps-start-date"
                />
                {projectStartDate && (
                  <div className="mt-2 space-y-1.5" data-testid="aps-working-calendar">
                    <label className="flex items-center gap-2 text-[11.5px] text-nexus-text-secondary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={skipWeekends}
                        onChange={(e) => setSkipWeekends(e.target.checked)}
                        disabled={isStreaming}
                        className="accent-nexus-accent-blue"
                        data-testid="aps-skip-weekends"
                      />
                      Skip weekend days:
                    </label>
                    {skipWeekends && (
                      <div className="flex items-center gap-1 flex-wrap" data-testid="aps-weekend-chips">
                        {[
                          { idx: 1, lbl: 'Mon' }, { idx: 2, lbl: 'Tue' }, { idx: 3, lbl: 'Wed' },
                          { idx: 4, lbl: 'Thu' }, { idx: 5, lbl: 'Fri' }, { idx: 6, lbl: 'Sat' },
                          { idx: 0, lbl: 'Sun' },
                        ].map(({ idx, lbl }) => {
                          const on = weekendDays.includes(idx);
                          return (
                            <button key={idx} disabled={isStreaming}
                              onClick={() => setWeekendDays((d) =>
                                d.includes(idx) ? d.filter((x) => x !== idx) : [...d, idx])}
                              className="text-[11px] px-2 py-0.5 rounded border"
                              style={on
                                ? { background: 'rgba(242,107,31,0.18)', borderColor: '#F26B1F', color: '#F8A66A' }
                                : { background: 'rgba(30,41,59,0.4)', borderColor: 'rgba(148,163,184,0.25)', color: '#94a3b8' }}>
                              {lbl}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <input
                      type="text"
                      value={holidaysText}
                      onChange={(e) => setHolidaysText(e.target.value)}
                      disabled={isStreaming}
                      placeholder="Holidays — comma-separated YYYY-MM-DD (e.g. 2026-03-25, 2026-08-15)"
                      className="w-full text-[11.5px] rounded-md px-2 py-1.5 focus:outline-none focus:border-nexus-accent-blue/60"
                      style={{
                        background: 'var(--nexus-input-bg)',
                        color: 'var(--nexus-text-primary)',
                        border: '1px solid var(--nexus-border-subtle)',
                      }}
                      data-testid="aps-holidays"
                    />
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">Preset:</span>
                      {['IN', 'US', 'EU'].map((cc) => (
                        <button
                          key={cc}
                          onClick={() => applyPreset(cc)}
                          disabled={isStreaming}
                          className="text-[11px] px-2 py-0.5 rounded border border-nexus-border-subtle hover:border-nexus-accent-blue/60 text-nexus-text-secondary hover:text-nexus-text-primary"
                          data-testid={`aps-preset-${cc.toLowerCase()}`}
                        >
                          🌐 {cc}
                        </button>
                      ))}
                      {holidaysText && (
                        <button onClick={() => setHolidaysText('')} className="text-[11px] px-2 py-0.5 text-nexus-text-tertiary hover:text-red-400" title="Clear holidays">
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Selected-element chip — appears the moment the user clicks
                a part in the viewer. Drives `element_id` on the next
                analyze call so the LLM sees that element's full
                properties. Has its own one-tap "Ask about this" button. */}
            {selectedElement && (
              <div className="px-3 pt-3" data-testid="aps-selected-element">
                <div className="rounded-lg p-2.5 border" style={{ background: 'rgba(242,107,31,0.08)', borderColor: 'rgba(242,107,31,0.35)' }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <MousePointerClick size={12} style={{ color: '#F26B1F' }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wider" style={{ color: '#F26B1F' }}>Selected element</div>
                      <div className="text-[12px] text-nexus-text-primary truncate" title={selectedElement.name}>
                        {selectedElement.name} <span className="text-nexus-text-tertiary">· id {selectedElement.dbId}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedElement(null)}
                      className="p-1 rounded-md hover:bg-nexus-bg-tertiary/40 text-nexus-text-tertiary"
                      title="Clear selection"
                    >
                      <X size={11} />
                    </button>
                  </div>
                  <button
                    onClick={() => runAnalysis('qa')}
                    disabled={isStreaming}
                    className="mt-2 w-full text-[12px] font-semibold px-3 py-1.5 rounded-md text-white disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #F26B1F, #FB8B3A)' }}
                    data-testid="aps-ask-about-element"
                  >
                    Ask about this element
                  </button>
                </div>
              </div>
            )}

            {/* Action grid — shown only when no thread is active */}
            {thread.length === 0 && (
              <>
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-2 p-3">
                  {ACTIONS.map((a) => (
                    <button
                      key={a.kind}
                      onClick={() => runAnalysis(a.kind)}
                      disabled={isStreaming || asset.status !== 'success'}
                      className="group text-left p-2.5 rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed border-nexus-border-subtle hover:border-nexus-accent-blue/50 hover:bg-nexus-bg-tertiary/30"
                      data-testid={`aps-action-${a.kind}`}
                    >
                      <div className="flex items-center gap-2">
                        <a.icon size={13} className="text-nexus-accent-blue" />
                        <span className="text-[12px] font-semibold text-nexus-text-primary">{a.label}</span>
                      </div>
                      <div className="text-[10.5px] text-nexus-text-tertiary mt-0.5 leading-snug">{a.desc}</div>
                    </button>
                  ))}
                </div>
                <div className="px-3 pb-2">
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Optional — refine the request (e.g. 'only structural elements', 'in metric units')"
                    rows={2}
                    disabled={isStreaming}
                    className="w-full text-[12px] rounded-md px-2.5 py-2 focus:outline-none focus:border-nexus-accent-blue/60 resize-none"
                    style={{
                      background: 'var(--nexus-input-bg)',
                      color: 'var(--nexus-text-primary)',
                      border: '1px solid var(--nexus-border-subtle)',
                    }}
                    data-testid="aps-action-prompt"
                  />
                </div>
              </>
            )}

            {/* Thread output — bubble-style multi-turn conversation */}
            <div className="flex-1 overflow-y-auto px-4 py-3 text-[12.5px] text-nexus-text-secondary">
              {/* Engine Optimizer — cost-saver advice card. Renders only
                  when the backend has enough usage signal (≥30 base cr
                  on a non-cheapest engine). Switching writes the chosen
                  model into the same localStorage slot the model selector
                  reads from, so the next analyze run uses it. */}
              <EngineOptimizerCard
                advice={engineAdvice}
                onSwitchEngine={(engineId) => {
                  try { localStorage.setItem('cognara.chat.model', engineId); } catch { /* ignore */ }
                  toast.success(`Switched to ${engineTier(engineId)} for next run`);
                  // Clear advice so the card hides until the next refresh
                  // confirms the heuristic is still valid post-switch.
                  setEngineAdvice((prev) => prev ? { ...prev, recommendation: null } : prev);
                }}
              />
              {thread.length === 0 && !isStreaming && (
                <div className="text-[12px] text-nexus-text-tertiary italic">
                  Pick an action above. The model context is automatically fed to Cognara — you don&apos;t need to paste anything.
                </div>
              )}
              {thread.map((m, i) => (
                <div key={i} className={`mb-4 ${m.role === 'user' ? 'flex justify-end' : ''}`}>
                  {m.role === 'user' ? (
                    <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-tr-sm text-[12px]"
                      style={{
                        background: 'rgba(30,78,140,0.14)',
                        color: 'var(--nexus-text-primary)',
                        border: '1px solid var(--hairline-strong, rgba(30,78,140,0.3))',
                      }}>
                      {m.content}
                    </div>
                  ) : (
                    <div className="aps-markdown prose prose-sm max-w-none dark:prose-invert" data-testid={`aps-msg-${i}`}>
                      {(() => {
                        const rows = parseTableRows(m.content);
                        if (!rows.length) return null;
                        const playingThis = playingMsgIdx === i;
                        return (
                          <div className="flex items-center justify-end gap-1.5 mb-1 flex-wrap">
                            {selectedKind === 'schedule' && (
                            <button
                              onClick={() => setAnimatorMsgIdx(i)}
                              disabled={playingThis}
                              className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border transition-all border-nexus-border-subtle text-nexus-text-tertiary hover:text-nexus-accent-blue hover:border-nexus-accent-blue/40 disabled:opacity-50"
                              title="Open the 4D Animator (hierarchical WBS picker + play)"
                              data-testid={`aps-msg-open-animator-${i}`}
                            >
                              <Film size={12} />
                              Open Animator · {rows.length} rows
                            </button>
                            )}
                            {playingThis && (
                              <button
                                onClick={() => runAnimation([], i)}
                                className="text-[11px] px-2 py-1 rounded border bg-orange-500/15 border-orange-500/60 text-orange-300"
                                data-testid={`aps-msg-stop-${i}`}
                              >
                                ⏸ Stop
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                        // Make every body-row of a table clickable + apply
                        // WBS-driven visual indentation. Click = isolate
                        // the 3D elements linked in the last cell.
                        tr: ({ node, children, ...props }) => {
                          const inBody = (node?.position && node.parent?.tagName === 'tbody') || true;
                          const cellTexts = (node?.children || []).map((c) =>
                            (c?.children || []).map((x) => x.value || '').join(' ').trim()
                          );
                          const text = cellTexts.join(' | ');
                          // Outline Level column (index 2 in the schedule
                          // format). Falls back to 1 for non-schedule
                          // tables (no padding added).
                          const outlineRaw = cellTexts[2] || '';
                          const outlineMatch = outlineRaw.match(/(\d+)/);
                          const outline = outlineMatch
                            ? Math.max(1, Math.min(parseInt(outlineMatch[1], 10), 8))
                            : null;
                          const label = cellTexts[3] || cellTexts[0] || 'row';
                          return (
                            <tr {...props}
                              data-outline={outline || undefined}
                              onClick={(e) => inBody && isolateForActivity(text, label, e.shiftKey)}
                              style={{ cursor: 'pointer' }}
                              title="Click to isolate · Shift-click to add">
                              {children}
                            </tr>
                          );
                        },
                      }}>{m.content}</ReactMarkdown>
                      {/* Interactive Clash Result table — appears for
                          clash threads only. Renders per-row Status pill
                          + Assignee dropdown + "Focus both" button on
                          top of the assistant's markdown output. Status
                          + assignee persist per (asset_id, clash_id) in
                          localStorage. */}
                      {selectedKind === 'clash' && m.role === 'assistant' && (() => {
                        const md = (m.content || '');
                        const lines = md.split('\n').filter((l) => l.trim().startsWith('|'));
                        // Skip separator + header row (first two pipe-lines).
                        const bodyLines = lines.filter((l) => !/^\|[\s\-:|]+\|$/.test(l.trim()));
                        if (bodyLines.length < 2) return null;
                        const header = bodyLines[0].split('|').map((s) => s.trim()).filter(Boolean);
                        const clashIdIdx = header.findIndex((h) => /clash\s*id|^id$/i.test(h));
                        if (clashIdIdx < 0) return null;
                        const typeIdx = header.findIndex((h) => /test\s*type|^type$/i.test(h));
                        const sevIdx = header.findIndex((h) => /severity/i.test(h));
                        const elAIdx = header.findIndex((h) => /element\s*a/i.test(h));
                        const elBIdx = header.findIndex((h) => /element\s*b/i.test(h));
                        const distIdx = header.findIndex((h) => /distance/i.test(h));
                        const parseObjId = (cell) => {
                          const m2 = String(cell || '').match(/\b(\d{2,7})\b/);
                          return m2 ? parseInt(m2[1], 10) : null;
                        };
                        const rows = bodyLines.slice(1).map((l) => {
                          const cells = l.split('|').map((s) => s.trim());
                          // Handle leading/trailing pipes → cells[0]='' and last=''
                          const c = cells.filter((_, i, a) => !(i === 0 && cells[0] === '') && !(i === a.length - 1 && cells[a.length - 1] === ''));
                          const cid = (c[clashIdIdx] || '').replace(/\*/g, '').trim();
                          if (!cid || !/[a-z0-9]/i.test(cid)) return null;
                          return {
                            id: cid,
                            type: typeIdx >= 0 ? c[typeIdx] : '',
                            severity: sevIdx >= 0 ? c[sevIdx] : '',
                            elemA: elAIdx >= 0 ? c[elAIdx] : '',
                            elemB: elBIdx >= 0 ? c[elBIdx] : '',
                            distance: distIdx >= 0 ? c[distIdx] : '',
                            idA: parseObjId(elAIdx >= 0 ? c[elAIdx] : ''),
                            idB: parseObjId(elBIdx >= 0 ? c[elBIdx] : ''),
                          };
                        }).filter(Boolean);
                        if (!rows.length) return null;
                        const STATUSES = [
                          { id: 'new',      label: 'New',      bg: 'rgba(59,130,246,0.18)',  fg: '#93C5FD', dot: '#3B82F6' },
                          { id: 'active',   label: 'Active',   bg: 'rgba(242,107,31,0.20)',  fg: '#F8A66A', dot: '#F26B1F' },
                          { id: 'reviewed', label: 'Reviewed', bg: 'rgba(124,58,237,0.18)',  fg: '#C4B5FD', dot: '#7C3AED' },
                          { id: 'approved', label: 'Approved', bg: 'rgba(16,185,129,0.18)',  fg: '#6EE7B7', dot: '#10B981' },
                          { id: 'resolved', label: 'Resolved', bg: 'rgba(148,163,184,0.18)', fg: '#94A3B8', dot: '#64748B' },
                        ];
                        const recordFor = (cid) => clashRecords[cid] || {};
                        const statusOf = (cid) => (recordFor(cid).status || 'new');
                        const statusFor = (cid) => STATUSES.find((s) => s.id === statusOf(cid)) || STATUSES[0];
                        // Filter-bar counts + gate.
                        const counts = STATUSES.reduce((acc, s) => { acc[s.id] = 0; return acc; }, {});
                        rows.forEach((r) => { counts[statusOf(r.id)] = (counts[statusOf(r.id)] || 0) + 1; });
                        const activeFilter = clashStatusFilter.size ? clashStatusFilter : null;
                        const visibleRows = activeFilter
                          ? rows.filter((r) => activeFilter.has(statusOf(r.id)))
                          : rows;
                        const isolateIds = (ids, fit = true) => {
                          const clean = (ids || []).filter((n) => typeof n === 'number' && n > 0);
                          if (!clean.length) { toast.error('No 3D object id could be extracted from that row'); return; }
                          try {
                            viewerRef.current?.isolate(clean);
                            if (fit) viewerRef.current?.fitToView(clean);
                          } catch (e) { console.error('clash isolate failed', e); }
                        };
                        return (
                          <div className="mt-4 rounded-lg border overflow-hidden"
                            style={{ borderColor: 'var(--nexus-border-subtle)', background: 'var(--nexus-bg-secondary)' }}
                            data-testid={`aps-clash-results-${i}`}>
                            <div className="px-3 py-2 border-b flex items-center justify-between flex-wrap gap-2"
                              style={{ borderColor: 'var(--nexus-border-subtle)' }}>
                              <div className="text-[11px] text-nexus-text-tertiary">
                                <span className="font-semibold text-nexus-text-secondary">Clash Results</span>
                                {' · '}{visibleRows.length}{activeFilter && visibleRows.length !== rows.length ? ` of ${rows.length}` : ''} interference{visibleRows.length === 1 ? '' : 's'}
                              </div>
                              <div className="inline-flex items-center gap-1 flex-wrap">
                                {STATUSES.map((opt) => {
                                  const on = clashStatusFilter.has(opt.id);
                                  const cnt = counts[opt.id] || 0;
                                  return (
                                    <button
                                      key={opt.id}
                                      onClick={() => setClashStatusFilter((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(opt.id)) next.delete(opt.id); else next.add(opt.id);
                                        return next;
                                      })}
                                      className="inline-flex items-center gap-1.5 text-[10.5px] px-2 py-1 rounded-full border transition-colors font-semibold"
                                      style={on
                                        ? { background: opt.bg, color: opt.fg, borderColor: opt.dot }
                                        : { background: 'transparent', color: 'var(--nexus-text-tertiary)', borderColor: 'var(--nexus-border-subtle)' }}
                                      title={on ? `Hide ${opt.label}` : `Show ${opt.label}`}
                                      data-testid={`aps-clash-filter-${opt.id}`}>
                                      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: opt.dot }} />
                                      {opt.label}
                                      <span className="opacity-70">{cnt}</span>
                                    </button>
                                  );
                                })}
                                {clashStatusFilter.size > 0 && (
                                  <button
                                    onClick={() => setClashStatusFilter(new Set())}
                                    className="text-[10.5px] text-nexus-accent-blue hover:underline ml-1"
                                    data-testid="aps-clash-filter-clear">
                                    All
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead>
                                  <tr className="text-nexus-text-tertiary" style={{ background: 'var(--nexus-bg-primary)' }}>
                                    <th className="px-2 py-1.5 text-left font-semibold">ID</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Type</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Severity</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Element A</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Element B</th>
                                    {distIdx >= 0 && <th className="px-2 py-1.5 text-left font-semibold">Distance</th>}
                                    <th className="px-2 py-1.5 text-left font-semibold">Status</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Assignee</th>
                                    <th className="px-2 py-1.5 text-right font-semibold">Focus</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visibleRows.map((r) => {
                                    const s = statusFor(r.id);
                                    const record = recordFor(r.id);
                                    const assignee = record.assignee_user_id || '';
                                    const rowMeta = {
                                      severity: r.severity, element_a: r.elemA,
                                      element_b: r.elemB, test_type: r.type,
                                    };
                                    return (
                                      <tr key={r.id} className="border-t hover:bg-white/[.03]"
                                        style={{ borderColor: 'var(--nexus-border-subtle)' }}
                                        data-testid={`aps-clash-row-${r.id}`}>
                                        <td className="px-2 py-1.5 font-mono text-nexus-text-primary">{r.id}</td>
                                        <td className="px-2 py-1.5 text-nexus-text-secondary">{r.type}</td>
                                        <td className="px-2 py-1.5 text-nexus-text-secondary">{r.severity}</td>
                                        <td className="px-2 py-1.5">
                                          <button
                                            onClick={() => isolateIds([r.idA])}
                                            disabled={!r.idA}
                                            className="text-left text-nexus-accent-blue hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                                            title={r.idA ? `Isolate #${r.idA}` : 'No objectid detected'}
                                            data-testid={`aps-clash-focus-a-${r.id}`}>
                                            {r.elemA || '—'}
                                          </button>
                                        </td>
                                        <td className="px-2 py-1.5">
                                          <button
                                            onClick={() => isolateIds([r.idB])}
                                            disabled={!r.idB}
                                            className="text-left text-nexus-accent-blue hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                                            title={r.idB ? `Isolate #${r.idB}` : 'No objectid detected'}
                                            data-testid={`aps-clash-focus-b-${r.id}`}>
                                            {r.elemB || '—'}
                                          </button>
                                        </td>
                                        {distIdx >= 0 && <td className="px-2 py-1.5 text-nexus-text-secondary font-mono">{r.distance}</td>}
                                        <td className="px-2 py-1.5">
                                          <select
                                            value={s.id}
                                            onChange={(e) => setStatusFor(r.id, e.target.value, rowMeta)}
                                            className="text-[10.5px] px-2 py-1 rounded-full font-semibold border cursor-pointer focus:outline-none"
                                            style={{ background: s.bg, color: s.fg, borderColor: s.dot }}
                                            data-testid={`aps-clash-status-${r.id}`}>
                                            {STATUSES.map((opt) => (
                                              <option key={opt.id} value={opt.id} style={{ background: 'var(--nexus-bg-primary)', color: 'var(--nexus-text-primary)' }}>
                                                {opt.label}
                                              </option>
                                            ))}
                                          </select>
                                        </td>
                                        <td className="px-2 py-1.5">
                                          <select
                                            value={assignee}
                                            onChange={(e) => setAssigneeFor(r.id, e.target.value, rowMeta)}
                                            className="text-[11px] px-2 py-1 rounded border cursor-pointer focus:outline-none"
                                            style={{ background: 'var(--nexus-input-bg)', color: 'var(--nexus-text-primary)', borderColor: 'var(--nexus-border-subtle)' }}
                                            data-testid={`aps-clash-assign-${r.id}`}>
                                            <option value="">Unassigned</option>
                                            {teamMembers.map((tm) => (
                                              <option key={tm.id} value={tm.id}>
                                                {tm.name || tm.email}{tm.role === 'admin' ? ' · admin' : ''}
                                              </option>
                                            ))}
                                          </select>
                                        </td>
                                        <td className="px-2 py-1.5 text-right">
                                          <button
                                            onClick={() => isolateIds([r.idA, r.idB].filter(Boolean))}
                                            disabled={!(r.idA || r.idB)}
                                            className="text-[10.5px] px-2 py-1 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                            style={{ background: 'rgba(30,78,140,0.14)', borderColor: 'rgba(30,78,140,0.4)', color: '#5FA8FF' }}
                                            title="Isolate + zoom to both elements in 3D"
                                            data-testid={`aps-clash-focus-both-${r.id}`}>
                                            Focus A+B
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {visibleRows.length === 0 && (
                                    <tr>
                                      <td colSpan={distIdx >= 0 ? 9 : 8}
                                        className="px-3 py-4 text-center text-nexus-text-tertiary text-[11px] italic">
                                        No clashes match the active status filter. Click "All" or a filter chip to widen the view.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Credit-deduction receipt chip — same component as
                          chat. Schedule / BoQ / Clash runs on Apex 4.8 can
                          easily burn 200+ credits; surfacing the cost
                          inline keeps users from guessing and reduces
                          unnecessary regenerations. */}
                      {m.meter && (() => {
                        const meter = m.meter;
                        const tier = meter.model === 'claude-opus-4-8' ? 'Apex 4.8'
                          : meter.model === 'claude-opus-4-7' ? 'Ultra 4.7'
                          : meter.model === 'claude-opus-4-6' ? 'Pro 4.6'
                          : (meter.model || '').replace('claude-', '').replace(/-/g, ' ');
                        const deducted = Number(meter.deducted ?? 0);
                        const base = Number(meter.base_credits ?? 0);
                        const mult = Number(meter.multiplier ?? 1);
                        return (
                          <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono text-nexus-text-tertiary hover:text-nexus-text-secondary transition-colors"
                               style={{ background: 'rgba(242,107,31,0.06)', border: '1px solid rgba(242,107,31,0.18)' }}
                               data-testid={`aps-credit-meter-chip-${i}`}
                               title={`Deducted ${deducted} credit${deducted === 1 ? '' : 's'} = ⌈${meter.tokens_total || 0} tokens / 1000⌉ (${base} base) × ${mult}× (${tier})`}>
                            <span style={{ color: '#F26B1F', fontWeight: 600 }}>−{deducted} cr</span>
                            <span className="opacity-60">·</span>
                            <span>{tier}</span>
                            <span className="opacity-60">·</span>
                            <span>{mult.toFixed(1)}×</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
              {isStreaming && (
                streamingAssistant ? (
                  <div className="aps-markdown prose prose-sm max-w-none dark:prose-invert" data-testid="aps-streaming">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingAssistant}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-nexus-text-tertiary">
                    <Loader2 className="animate-spin" size={13} /> Thinking…
                  </div>
                )
              )}
            </div>

            {/* Footer — stop / follow-up composer / transfer button */}
            {(thread.length > 0 || isStreaming) && (
              <div className="border-t border-nexus-border-subtle p-3 space-y-2 bg-nexus-bg-primary/40">
                {isStreaming ? (
                  <button onClick={stopAnalysis}
                    className="w-full text-[12px] px-3 py-2 rounded-md bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20"
                    data-testid="aps-stop">
                    Stop streaming
                  </button>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        value={followUp}
                        onChange={(e) => setFollowUp(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runFollowUp(); } }}
                        placeholder="Refine — e.g. 'make durations 20% more conservative' or 'add critical path'"
                        className="flex-1 text-[12px] rounded-md px-3 py-2 focus:outline-none focus:border-nexus-accent-blue/60"
                        style={{
                          background: 'var(--nexus-input-bg)',
                          color: 'var(--nexus-text-primary)',
                          border: '1px solid var(--nexus-border-subtle)',
                        }}
                        data-testid="aps-followup-input"
                      />
                      <button onClick={runFollowUp} disabled={!followUp.trim() || !selectedKind}
                        className="px-3 py-2 rounded-md text-white text-[12px] font-semibold disabled:opacity-40"
                        style={{ background: 'linear-gradient(135deg, #1E4E8C, #2563EB)' }}
                        data-testid="aps-followup-send">
                        Send
                      </button>
                    </div>
                    <button onClick={transferToChat}
                      disabled={transferring || thread.filter((m) => m.role === 'assistant').length === 0}
                      className="w-full inline-flex items-center justify-center gap-2 text-[12px] px-3 py-2 rounded-md font-semibold border transition-all disabled:opacity-40"
                      style={{ background: 'rgba(242,107,31,0.10)', borderColor: 'rgba(242,107,31,0.40)', color: '#F8A66A' }}
                      data-testid="aps-transfer-to-chat">
                      {transferring ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
                      Send to Knowledge Chat
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Animator modal — opens when user clicks "Open Animator" on a table message */}
        <AnimatorPane
          open={animatorMsgIdx !== null}
          onClose={() => setAnimatorMsgIdx(null)}
          rows={animatorMsgIdx !== null ? parseTableRows(thread[animatorMsgIdx]?.content || '') : []}
          meter={animatorMsgIdx !== null ? (thread[animatorMsgIdx]?.meter || null) : null}
          playing={isPlaying && playingMsgIdx === animatorMsgIdx}
          onPlay={(startIdx) => {
            const rows = parseTableRows(thread[animatorMsgIdx]?.content || '');
            setAnimatorMsgIdx(null);
            runAnimation(rows, animatorMsgIdx, startIdx);
          }}
          onStop={() => runAnimation([], animatorMsgIdx)}
        />
        {/* Prescope modal — Navisworks-Manage-style branch + property
            picker. Opens when the user clicks Generate BoQ or Clash
            detect so they can narrow the analysis before Opus fires.
            Selection is empty by default (whole model). */}
        {prescope.open && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(4px)' }}
            onClick={() => setPrescope((p) => ({ ...p, open: false }))}
            data-testid="aps-prescope-modal"
          >
            <div
              className="w-full max-w-3xl rounded-xl border overflow-hidden flex flex-col"
              style={{
                background: 'var(--nexus-bg-primary)',
                borderColor: 'var(--nexus-border-subtle)',
                maxHeight: '85vh',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-3 border-b flex items-center justify-between"
                style={{ borderColor: 'var(--nexus-border-subtle)', background: 'var(--nexus-bg-secondary)' }}>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                    Cognara · Scoped {prescope.kind === 'boq' ? 'Bill of Quantities' : 'Clash Detection'}
                  </div>
                  <div className="text-[15px] font-semibold text-nexus-text-primary">
                    {prescope.kind === 'clash' ? 'Configure clash test' : 'Pick model branches & properties'}
                  </div>
                </div>
                <button onClick={() => setPrescope((p) => ({ ...p, open: false }))}
                  className="p-1 rounded-md hover:bg-nexus-bg-tertiary text-nexus-text-tertiary"
                  data-testid="aps-prescope-close">
                  <X size={14} />
                </button>
              </div>
              {prescope.kind === 'clash' && (
                <div className="px-5 py-3 border-b flex flex-wrap items-end gap-4"
                  style={{ borderColor: 'var(--nexus-border-subtle)', background: 'var(--nexus-bg-primary)' }}
                  data-testid="aps-clash-config">
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold">
                      Test type
                    </label>
                    <div className="inline-flex rounded-md border overflow-hidden text-[11.5px]"
                      style={{ borderColor: 'var(--nexus-border-subtle)' }}>
                      {[
                        { id: 'hard', label: 'Hard' },
                        { id: 'clearance', label: 'Clearance' },
                        { id: 'duplicates', label: 'Duplicates' },
                      ].map((t) => (
                        <button
                          key={t.id}
                          onClick={() => setPrescope((p) => ({ ...p, testType: t.id }))}
                          className="px-3 py-1.5 transition-colors"
                          style={prescope.testType === t.id
                            ? { background: '#F26B1F', color: 'white', fontWeight: 600 }
                            : { background: 'var(--nexus-bg-secondary)', color: 'var(--nexus-text-secondary)' }}
                          data-testid={`aps-clash-test-${t.id}`}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {prescope.testType === 'clearance' && (
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold">
                        Tolerance (mm)
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={prescope.tolerance}
                        onChange={(e) => setPrescope((p) => ({ ...p, tolerance: Number(e.target.value) || 25 }))}
                        className="text-[11.5px] px-2 py-1.5 rounded-md focus:outline-none focus:border-nexus-accent-blue/60 w-24"
                        style={{ background: 'var(--nexus-input-bg)', color: 'var(--nexus-text-primary)', border: '1px solid var(--nexus-border-subtle)' }}
                        data-testid="aps-clash-tolerance"
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold">
                      Selection set to edit — click branches in the tree
                    </label>
                    <div className="inline-flex rounded-md border overflow-hidden text-[11.5px]"
                      style={{ borderColor: 'var(--nexus-border-subtle)' }}>
                      <button
                        onClick={() => setPrescope((p) => ({ ...p, activeSet: 'A' }))}
                        className="px-3 py-1.5 transition-colors"
                        style={prescope.activeSet === 'A'
                          ? { background: '#1E4E8C', color: 'white', fontWeight: 600 }
                          : { background: 'var(--nexus-bg-secondary)', color: 'var(--nexus-text-secondary)' }}
                        data-testid="aps-clash-set-A">
                        Selection A · {prescope.paths.size || 'all'}
                      </button>
                      <button
                        onClick={() => setPrescope((p) => ({ ...p, activeSet: 'B' }))}
                        className="px-3 py-1.5 transition-colors"
                        style={prescope.activeSet === 'B'
                          ? { background: '#7C3AED', color: 'white', fontWeight: 600 }
                          : { background: 'var(--nexus-bg-secondary)', color: 'var(--nexus-text-secondary)' }}
                        data-testid="aps-clash-set-B">
                        Selection B · {prescope.pathsB.size || 'all'}
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 text-[11px] text-nexus-text-tertiary leading-snug min-w-[200px]">
                    <span className="font-semibold text-nexus-text-secondary">Tip:</span> select equipment/piping in <span style={{color:'#5FA8FF'}}>A</span> and structural/civil in <span style={{color:'#B892FF'}}>B</span> — leave either empty for "entire model" on that side. Clash pairs will be scoped strictly to A × B.
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-5 gap-0"
                style={{ minHeight: '400px' }}>
                {/* Hierarchy tree */}
                <div className="md:col-span-3 border-r overflow-y-auto p-3"
                  style={{ borderColor: 'var(--nexus-border-subtle)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold">
                      Model hierarchy ({treeLines.length} nodes)
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCollapsedNodes(new Set())}
                        className="text-[10.5px] text-nexus-accent-blue hover:underline"
                        data-testid="aps-prescope-expand-all">
                        Expand all
                      </button>
                      <button
                        onClick={() => {
                          const all = new Set();
                          treeLines.forEach((n) => { if (n.hasChildren) all.add(n.objectid); });
                          setCollapsedNodes(all);
                        }}
                        className="text-[10.5px] text-nexus-accent-blue hover:underline"
                        data-testid="aps-prescope-collapse-all">
                        Collapse all
                      </button>
                      <button
                        onClick={() => setPrescope((p) => ({ ...p, paths: new Set(), pathsB: new Set() }))}
                        className="text-[10.5px] text-nexus-accent-blue hover:underline"
                        data-testid="aps-prescope-clear-paths">
                        Clear
                      </button>
                    </div>
                  </div>
                  {/* Tree search */}
                  <div className="relative mb-2">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-nexus-text-tertiary" />
                    <input
                      type="text"
                      value={treeSearch}
                      onChange={(e) => setTreeSearch(e.target.value)}
                      placeholder="Search branches…"
                      className="w-full text-[11.5px] pl-7 pr-2 py-1.5 rounded-md focus:outline-none focus:border-nexus-accent-blue/60"
                      style={{
                        background: 'var(--nexus-input-bg)',
                        color: 'var(--nexus-text-primary)',
                        border: '1px solid var(--nexus-border-subtle)',
                      }}
                      data-testid="aps-prescope-tree-search"
                    />
                  </div>
                  {treeLines.length === 0 ? (
                    <div className="text-[12px] text-nexus-text-tertiary italic p-3">
                      No hierarchy tree indexed for this asset. Running with no path filter will scan the entire model.
                    </div>
                  ) : (() => {
                    // Compute the visible node set. Search auto-expands
                    // ancestors of every match so hits are never hidden
                    // behind a collapsed parent (iter52 HIGH bug).
                    const search = treeSearch.trim().toLowerCase();
                    const matchIdx = new Set();
                    const revealed = new Set();   // ancestor objectids forced-open by an active search
                    if (search) {
                      treeLines.forEach((n, i) => {
                        if (n.name.toLowerCase().includes(search) || String(n.objectid).includes(search)) {
                          matchIdx.add(i);
                          // Walk backwards to reveal every ancestor.
                          let ancestorDepth = n.depth;
                          for (let j = i - 1; j >= 0 && ancestorDepth > 0; j -= 1) {
                            if (treeLines[j].depth < ancestorDepth) {
                              matchIdx.add(j);
                              revealed.add(treeLines[j].objectid);
                              ancestorDepth = treeLines[j].depth;
                            }
                          }
                        }
                      });
                    }
                    // Precompute a depth stack so we can decide
                    // "is this node under a currently-collapsed
                    // parent?" in O(1) per node instead of O(N)
                    // treeLines.find lookups (iter52 code-review note).
                    const collapsedStack = [];
                    const rows = [];
                    treeLines.forEach((n, i) => {
                      while (collapsedStack.length && n.depth <= collapsedStack[collapsedStack.length - 1].depth) {
                        collapsedStack.pop();
                      }
                      const underCollapsed = collapsedStack.length > 0;
                      const visibleBySearch = !search || matchIdx.has(i);
                      if (!underCollapsed && visibleBySearch) rows.push({ n, i });
                      // A search-revealed ancestor is treated as
                      // expanded regardless of collapsedNodes state.
                      const effectivelyCollapsed = collapsedNodes.has(n.objectid) && !revealed.has(n.objectid);
                      if (effectivelyCollapsed && n.hasChildren) {
                        collapsedStack.push(n);
                      }
                    });
                    return (
                      <div className="space-y-0.5" style={{ fontFamily: 'var(--font-mono)' }}>
                        {rows.slice(0, 800).map(({ n, i }) => {
                          const inA = prescope.paths.has(n.objectid);
                          const inB = prescope.pathsB.has(n.objectid);
                          const isClash = prescope.kind === 'clash';
                          const activeIsA = prescope.activeSet === 'A';
                          const checked = isClash ? (activeIsA ? inA : inB) : inA;
                          const collapsed = collapsedNodes.has(n.objectid);
                          return (
                            <div
                              key={n.objectid}
                              className="flex items-center gap-1 text-[11.5px] py-1 px-1 rounded hover:bg-nexus-bg-tertiary/40"
                              style={{ paddingLeft: `${4 + n.depth * 14}px` }}
                              data-testid={`aps-prescope-node-${n.objectid}`}>
                              {n.hasChildren ? (
                                <button
                                  onClick={() => setCollapsedNodes((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(n.objectid)) next.delete(n.objectid);
                                    else next.add(n.objectid);
                                    return next;
                                  })}
                                  className="p-0.5 text-nexus-text-tertiary hover:text-nexus-text-primary"
                                  data-testid={`aps-prescope-chevron-${n.objectid}`}>
                                  {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                </button>
                              ) : (
                                <span className="w-[18px] inline-block" />
                              )}
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setPrescope((p) => {
                                    // For clash, write into the active
                                    // set (A or B). For BoQ, always A.
                                    const targetKey = (p.kind === 'clash' && p.activeSet === 'B') ? 'pathsB' : 'paths';
                                    const next = new Set(p[targetKey]);
                                    const branch = treeLines.slice(i, n.descendantEnd);
                                    if (e.target.checked) branch.forEach((b) => next.add(b.objectid));
                                    else branch.forEach((b) => next.delete(b.objectid));
                                    return { ...p, [targetKey]: next };
                                  });
                                }}
                                className="accent-nexus-accent-blue cursor-pointer"
                              />
                              <span className="text-nexus-text-primary flex-1">{_highlight(n.name, treeSearch)}</span>
                              {isClash && inA && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                                  style={{ background: 'rgba(30,78,140,0.25)', color: '#5FA8FF' }}
                                  title="Member of Selection A">A</span>
                              )}
                              {isClash && inB && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                                  style={{ background: 'rgba(124,58,237,0.25)', color: '#B892FF' }}
                                  title="Member of Selection B">B</span>
                              )}
                              <span className="text-nexus-text-tertiary text-[10px]">#{_highlight(String(n.objectid), treeSearch)}</span>
                            </div>
                          );
                        })}
                        {rows.length > 800 && (
                          <div className="text-[10.5px] text-nexus-text-tertiary italic p-2">
                            Showing first 800 visible nodes. Use search or collapse parents to drill deeper.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {/* Property picker */}
                <div className="md:col-span-2 overflow-y-auto p-3">
                  {(() => {
                    // Lazy-loader for the value drill-down. Fetches on
                    // demand and memoises in `valueCache` so subsequent
                    // opens are instant.
                    const openDrilldown = async (key) => {
                      setDrilldownKey(key);
                      setDrilldownSearch('');
                      if (valueCache[`${key}::`]) return;
                      setDrilldownLoading(true);
                      try {
                        const resp = await portalAssetsApi.propertyValues(asset.id, key, { limit: 500 });
                        setValueCache((prev) => ({
                          ...prev,
                          [`${key}::`]: {
                            values: resp.data?.values || [],
                            total_unique: resp.data?.total_unique || 0,
                          },
                        }));
                      } catch (e) {
                        toast.error(`Couldn't load values for ${key}`);
                      } finally {
                        setDrilldownLoading(false);
                      }
                    };
                    const closeDrilldown = () => { setDrilldownKey(null); setDrilldownSearch(''); };
                    const totalPickedValues = Object.values(prescope.propertyValues || {})
                      .reduce((acc, s) => acc + (s?.size || 0), 0);
                    if (drilldownKey) {
                      const q = drilldownSearch.trim();
                      // Prefer the search-specific cache when present so
                      // values only found via server-side search render.
                      const searchCache = q ? valueCache[`${drilldownKey}::${q.toLowerCase()}`] : null;
                      const baseCache = valueCache[`${drilldownKey}::`] || { values: [], total_unique: 0 };
                      const cached = searchCache || baseCache;
                      const list = cached.values;
                      const clientFiltered = q && !searchCache
                        ? list.filter((v) => String(v.value).toLowerCase().includes(q.toLowerCase()))
                        : list;
                      const filteredVals = clientFiltered;
                      const selected = prescope.propertyValues[drilldownKey] || new Set();
                      return (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <button
                              onClick={closeDrilldown}
                              className="text-[11px] text-nexus-accent-blue hover:underline inline-flex items-center gap-1"
                              data-testid="aps-prescope-drilldown-back">
                              ← Back to properties
                            </button>
                          </div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold">
                              {drilldownKey} · values
                              <span className="ml-1 opacity-60">({cached.total_unique || list.length} unique)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setPrescope((p) => {
                                  const next = { ...(p.propertyValues || {}) };
                                  const cur = new Set(next[drilldownKey] || []);
                                  filteredVals.forEach((v) => cur.add(v.value));
                                  next[drilldownKey] = cur;
                                  const props = new Set(p.properties);
                                  if (cur.size > 0) props.add(drilldownKey);
                                  return { ...p, propertyValues: next, properties: props };
                                })}
                                className="text-[10.5px] text-nexus-accent-blue hover:underline"
                                data-testid="aps-prescope-drilldown-selectall">
                                Select all
                              </button>
                              <button
                                onClick={() => setPrescope((p) => {
                                  const next = { ...(p.propertyValues || {}) };
                                  delete next[drilldownKey];
                                  return { ...p, propertyValues: next };
                                })}
                                className="text-[10.5px] text-nexus-text-tertiary hover:text-nexus-text-primary"
                                data-testid="aps-prescope-drilldown-clear">
                                Clear
                              </button>
                            </div>
                          </div>
                          <div className="text-[10.5px] text-nexus-text-tertiary mb-2 leading-snug">
                            Pick specific values — the BoQ / clash will include ONLY elements whose <span className="font-semibold">{drilldownKey}</span> matches one of your picks.
                          </div>
                          <div className="relative mb-2">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-nexus-text-tertiary" />
                            <input
                              type="text"
                              value={drilldownSearch}
                              onChange={(e) => setDrilldownSearch(e.target.value)}
                              placeholder={`Search ${cached.total_unique || list.length} values…`}
                              className="w-full text-[11.5px] pl-7 pr-2 py-1.5 rounded-md focus:outline-none focus:border-nexus-accent-blue/60"
                              style={{
                                background: 'var(--nexus-input-bg)',
                                color: 'var(--nexus-text-primary)',
                                border: '1px solid var(--nexus-border-subtle)',
                              }}
                              data-testid="aps-prescope-drilldown-search"
                            />
                          </div>
                          {drilldownLoading && (
                            <div className="text-[11px] text-nexus-text-tertiary italic p-2 flex items-center gap-2">
                              <Loader2 size={12} className="animate-spin" /> Loading values…
                            </div>
                          )}
                          {!drilldownLoading && filteredVals.length === 0 && (
                            <div className="text-[11px] text-nexus-text-tertiary italic p-2">
                              {q ? `No values match "${drilldownSearch}"` : 'No values found for this property.'}
                            </div>
                          )}
                          {!drilldownLoading && filteredVals.length > 0 && (
                            <div className="flex flex-col gap-0.5 max-h-[52vh] overflow-y-auto pr-1">
                              {filteredVals.slice(0, 500).map((v) => {
                                const on = selected.has(v.value);
                                return (
                                  <label
                                    key={v.value}
                                    className="flex items-center gap-2 text-[11.5px] px-2 py-1 rounded cursor-pointer hover:bg-white/5"
                                    data-testid={`aps-prescope-value-${v.value}`}>
                                    <input
                                      type="checkbox"
                                      checked={on}
                                      onChange={() => setPrescope((p) => {
                                        const next = { ...(p.propertyValues || {}) };
                                        const cur = new Set(next[drilldownKey] || []);
                                        if (on) cur.delete(v.value); else cur.add(v.value);
                                        if (cur.size) next[drilldownKey] = cur;
                                        else delete next[drilldownKey];
                                        const props = new Set(p.properties);
                                        if (cur.size > 0) props.add(drilldownKey);
                                        return { ...p, propertyValues: next, properties: props };
                                      })}
                                      className="accent-nexus-accent-blue cursor-pointer"
                                    />
                                    <span className="text-nexus-text-primary flex-1 truncate" title={v.value}>
                                      {_highlight(v.value, drilldownSearch)}
                                    </span>
                                    <span className="text-nexus-text-tertiary text-[10px]">×{v.count}</span>
                                  </label>
                                );
                              })}
                              {filteredVals.length > 500 && (
                                <div className="text-[10.5px] text-nexus-text-tertiary italic p-2">
                                  Showing first 500 of {filteredVals.length}. Refine the search to narrow further.
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-2 text-[10.5px] text-nexus-text-tertiary border-t pt-2"
                            style={{ borderColor: 'var(--nexus-border-subtle)' }}>
                            {selected.size > 0
                              ? <>Selected <span className="text-[#F26B1F] font-semibold">{selected.size}</span> value{selected.size === 1 ? '' : 's'} for {drilldownKey}.</>
                              : <>No values selected yet — pick at least one to apply the filter.</>}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {!drilldownKey && (<>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold">
                      Project properties ({propertyKeys.length})
                      {Object.keys(prescope.propertyValues || {}).length > 0 && (
                        <span className="ml-2 text-[#F26B1F] normal-case tracking-normal">
                          · {Object.values(prescope.propertyValues).reduce((a, s) => a + s.size, 0)} value filter{Object.values(prescope.propertyValues).reduce((a, s) => a + s.size, 0) === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setPrescope((p) => ({ ...p, properties: new Set(), propertyValues: {} }))}
                      className="text-[10.5px] text-nexus-accent-blue hover:underline"
                      data-testid="aps-prescope-clear-props">
                      Clear
                    </button>
                  </div>
                  <div className="text-[10.5px] text-nexus-text-tertiary mb-2 leading-snug">
                    Optional. Tap a chip to project that column · tap the drill-down icon to filter by specific values.
                  </div>
                  <div className="relative mb-2">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-nexus-text-tertiary" />
                    <input
                      type="text"
                      value={propSearch}
                      onChange={(e) => setPropSearch(e.target.value)}
                      placeholder="Search properties…"
                      className="w-full text-[11.5px] pl-7 pr-2 py-1.5 rounded-md focus:outline-none focus:border-nexus-accent-blue/60"
                      style={{
                        background: 'var(--nexus-input-bg)',
                        color: 'var(--nexus-text-primary)',
                        border: '1px solid var(--nexus-border-subtle)',
                      }}
                      data-testid="aps-prescope-prop-search"
                    />
                  </div>
                  {(() => {
                    const openDrilldownInner = async (key) => {
                      setDrilldownKey(key);
                      setDrilldownSearch('');
                      if (valueCache[`${key}::`]) return;
                      setDrilldownLoading(true);
                      try {
                        const resp = await portalAssetsApi.propertyValues(asset.id, key, { limit: 500 });
                        setValueCache((prev) => ({
                          ...prev,
                          [`${key}::`]: {
                            values: resp.data?.values || [],
                            total_unique: resp.data?.total_unique || 0,
                          },
                        }));
                      } catch (e) {
                        toast.error(`Couldn't load values for ${key}`);
                      } finally {
                        setDrilldownLoading(false);
                      }
                    };
                    // Group properties by their `group` (Derivative API
                    // classification: e.g. "Item", "Material", "Dimensions").
                    const q = propSearch.trim().toLowerCase();
                    const filtered = propertyKeys.filter((p) =>
                      !q || p.key.toLowerCase().includes(q) || (p.group || '').toLowerCase().includes(q));
                    const cap = propShowAll ? filtered.length : 80;
                    const shown = filtered.slice(0, cap);
                    const byGroup = shown.reduce((acc, p) => {
                      const g = p.group || 'Other';
                      (acc[g] = acc[g] || []).push(p);
                      return acc;
                    }, {});
                    const groups = Object.keys(byGroup).sort();
                    return (
                      <div className="space-y-3">
                        {groups.map((g) => (
                          <div key={g}>
                            <div className="text-[9.5px] uppercase tracking-wider text-nexus-text-tertiary font-bold mb-1 border-b pb-0.5"
                              style={{ borderColor: 'var(--nexus-border-subtle)' }}>
                              {g} <span className="opacity-60">· {byGroup[g].length}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {byGroup[g].map((p) => {
                                const on = prescope.properties.has(p.key);
                                const valCount = (prescope.propertyValues[p.key]?.size) || 0;
                                return (
                                  <div
                                    key={`${p.group}::${p.key}`}
                                    className="inline-flex items-center rounded border overflow-hidden"
                                    style={on || valCount
                                      ? { background: 'rgba(242,107,31,0.15)', borderColor: '#F26B1F', color: '#F26B1F' }
                                      : { background: 'var(--nexus-bg-secondary)', borderColor: 'var(--nexus-border-subtle)', color: 'var(--nexus-text-secondary)' }}>
                                    <button
                                      onClick={() => {
                                        setPrescope((cur) => {
                                          const next = new Set(cur.properties);
                                          if (on) next.delete(p.key);
                                          else next.add(p.key);
                                          return { ...cur, properties: next };
                                        });
                                      }}
                                      className="text-[11px] px-2 py-1 inline-flex items-center gap-1 transition-colors hover:brightness-110"
                                      title={on ? 'Deselect column' : 'Project this column'}
                                      data-testid={`aps-prescope-prop-${p.key}`}>
                                      {_propTypeIcon(p)}
                                      <span>{_highlight(p.key, propSearch)}</span>
                                      <span className="opacity-60">×{p.count}</span>
                                    </button>
                                    <button
                                      onClick={() => openDrilldownInner(p.key)}
                                      className="text-[11px] px-1.5 py-1 border-l inline-flex items-center gap-0.5 hover:brightness-125"
                                      style={{ borderColor: 'currentColor' }}
                                      title={`Drill into ${p.key} values`}
                                      data-testid={`aps-prescope-prop-drill-${p.key}`}>
                                      {valCount > 0 && <span className="font-semibold">{valCount}</span>}
                                      <ChevronRight size={11} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        {filtered.length > 80 && !propShowAll && (
                          <button
                            onClick={() => setPropShowAll(true)}
                            className="text-[11px] text-nexus-accent-blue hover:underline mt-2"
                            data-testid="aps-prescope-prop-showall">
                            Show all {filtered.length} properties
                          </button>
                        )}
                        {filtered.length === 0 && (
                          <div className="text-[11px] text-nexus-text-tertiary italic">No properties match "{propSearch}"</div>
                        )}
                      </div>
                    );
                  })()}
                  </>)}
                </div>
              </div>
              <div className="px-5 py-3 border-t"
                style={{ borderColor: 'var(--nexus-border-subtle)', background: 'var(--nexus-bg-secondary)' }}>
                {/* Presets bar */}
                <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="aps-prescope-presets">
                  <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold mr-1">
                    Presets
                  </span>
                  {presets.length === 0 && (
                    <span className="text-[10.5px] text-nexus-text-tertiary italic">
                      No saved presets yet. Save your first below.
                    </span>
                  )}
                  {presets.map((preset) => (
                    <div key={preset.name}
                      className="text-[10.5px] px-2 py-1 rounded-md border flex items-center gap-1"
                      style={{ background: 'var(--nexus-bg-primary)', borderColor: 'var(--nexus-border-subtle)' }}
                      data-testid={`aps-prescope-preset-${preset.name}`}>
                      <button onClick={() => applyScopePreset(preset)}
                        className="text-nexus-text-primary hover:text-nexus-accent-blue">
                        {preset.name} <span className="opacity-60">({(preset.paths || []).length})</span>
                      </button>
                      <button onClick={() => deleteScopePreset(preset.name)}
                        className="text-nexus-text-tertiary hover:text-red-400" title="Delete preset">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  <button onClick={saveScopePreset}
                    disabled={prescope.paths.size === 0 && prescope.properties.size === 0 && Object.keys(prescope.propertyValues || {}).length === 0}
                    className="text-[10.5px] px-2 py-1 rounded-md border text-nexus-accent-blue hover:bg-nexus-accent-blue/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ borderColor: 'var(--nexus-border-subtle)' }}
                    data-testid="aps-prescope-save-preset">
                    + Save current
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] text-nexus-text-tertiary">
                    {(() => {
                      const valCount = Object.values(prescope.propertyValues || {}).reduce((a, s) => a + (s?.size || 0), 0);
                      const bits = [];
                      if (prescope.paths.size) bits.push(`${prescope.paths.size} branch${prescope.paths.size === 1 ? '' : 'es'}`);
                      if (prescope.properties.size) bits.push(`${prescope.properties.size} propert${prescope.properties.size === 1 ? 'y' : 'ies'}`);
                      if (valCount) bits.push(`${valCount} value filter${valCount === 1 ? '' : 's'}`);
                      if (!bits.length) return 'No filters set — will analyse the full model.';
                      return `Scoped to ${bits.join(' · ')}`;
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPrescope((p) => ({ ...p, open: false }))}
                      className="text-[12px] px-3 py-1.5 rounded-md border text-nexus-text-secondary hover:text-nexus-text-primary"
                      style={{ borderColor: 'var(--nexus-border-subtle)' }}
                      data-testid="aps-prescope-cancel">
                      Cancel
                    </button>
                    <button
                      onClick={runScopedAnalysis}
                      className="text-[12px] px-3 py-1.5 rounded-md text-white font-semibold"
                      style={{ background: 'linear-gradient(135deg, #1E4E8C, #2563EB)' }}
                      data-testid="aps-prescope-run">
                      Run {prescope.kind === 'boq' ? 'BoQ' : 'Clash Detection'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
